import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { auth, db, getFirebaseAuthHeaders } from '../firebase';
import { doc, setDoc, serverTimestamp, collection, addDoc, onSnapshot, runTransaction } from 'firebase/firestore';
import { scanReportsService } from '../services/scanReports';
import { groupReviewsService } from '../services/groupReviews';

/* ─────────────────────────────────────────────────────────────── */
const BACKEND_ROOT = ((process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000')
  .trim()
  .replace(/\/api\/?$/, ''));

/* ── pure helpers ────────────────────────────────────────────── */
const toProgressPercent = (v) => {
  const n = Number.parseFloat(v);
  if (!Number.isFinite(n)) return 0;
  return Math.min(100, Math.max(0, n <= 1 ? n * 100 : n));
};

const deriveProgressPercent = (progress) => {
  const completed = Number(progress?.completed_items);
  const total = Number(progress?.total_items);
  const phase = String(progress?.current_phase || '').trim();
  const phaseFloor = {
    initializing: 2,
    translating: 8,
    discovering_groups: 18,
    preparing_scan: 32,
    scanning_groups: 36,
    generating_report: 96,
    resuming: 0,
  }[phase] ?? 0;

  if (Number.isFinite(completed) && Number.isFinite(total) && total > 0) {
    const raw = (completed / total) * 100;
    return Math.min(100, Math.max(phaseFloor, raw, 0));
  }
  return Math.max(phaseFloor, toProgressPercent(progress?.percentage ?? progress?.progress));
};

const formatWaitDuration = (secondsValue) => {
  const seconds = Number(secondsValue);
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  if (seconds >= 3600) {
    const hours = seconds / 3600;
    return `${hours.toFixed(hours >= 10 ? 0 : 1)} hour${hours >= 2 ? 's' : ''}`;
  }
  if (seconds >= 60) {
    const minutes = seconds / 60;
    return `${minutes.toFixed(minutes >= 10 ? 0 : 1)} minute${minutes >= 2 ? 's' : ''}`;
  }
  return `${Math.round(seconds)} second${Math.round(seconds) === 1 ? '' : 's'}`;
};

const formatUiError = (rawMessage) => {
  const text = String(rawMessage || '').trim();
  if (!text) return 'Something went wrong. Please try again.';

  const waitMatch = text.match(/wait of\s+(\d+)\s+seconds/i);
  if (waitMatch) {
    const humanWait = formatWaitDuration(waitMatch[1]);
    const causedByMatch = text.match(/\(caused by ([^)]+)\)/i);
    const causeLabel = causedByMatch?.[1] ? ` while resolving Telegram groups` : '';
    return `Telegram has temporarily rate-limited this account${causeLabel}. Please wait about ${humanWait} before starting another scan.`;
  }

  if (/failed before completion/i.test(text)) {
    return 'The scan stopped before finishing. Please try again in a moment.';
  }

  return text;
};

/* ── AI Phrase cleanup */
const sanitizePhraseToken = (value) =>
  String(value || '')
    .trim()
    .replace(/^[-*\u2022]?\s*\d+[.)-]?\s*/, '')
    .replace(/^phrases?\s*:\s*\d+\s*/i, '')
    .replace(/^["'`]+|["'`]+$/g, '')
    .replace(/^[[(]+|[)\]]+$/g, '')
    .replace(/\s+/g, ' ')
    .trim();


/** 
 * NEW FORMULA: risk = total_hits + phrase_hits + external_links
 * Thresholds aligned with backend: 70+ Critical, 45+ High, 20+ Medium, else Low
 */
/** 
 * NEW THRESHOLDS: < 5 low, < 15 medium, < 25 high, 25+ critical
 */
const hitsRiskLevel = (score) => {
  const n = Number(score) || 0;
  if (n >= 25) return 'critical';
  if (n >= 15) return 'high';
  if (n >= 5)  return 'medium';
  return 'low';
};

const RISK_RANK  = { critical: 4, high: 3, medium: 2, low: 1 };
const AI_SORT_RANK = { fraud: 3, uncertain: 2, genuine: 1, unknown: 0 };
const riskColour = (r) => ({ critical: '#dc2626', high: '#d97706', medium: '#0369a1', low: '#16a34a' }[r] || '#64748b');
const langFlag   = (l) => ({ EN: '🇬🇧', PT: '🇵🇹', RU: '🇷🇺' }[(l || '').toUpperCase()] || '🌐');

const LANGUAGE_OPTIONS = [
  { key: 'english', label: 'English' },
  { key: 'hindi', label: 'Hindi' },
  { key: 'konkani', label: 'Konkani' },
  { key: 'marathi', label: 'Marathi' },
  { key: 'gujarati', label: 'Gujarati' },
  { key: 'punjabi', label: 'Punjabi' },
  { key: 'bengali', label: 'Bengali' },
  { key: 'tamil', label: 'Tamil' },
  { key: 'telugu', label: 'Telugu' },
  { key: 'kannada', label: 'Kannada' },
  { key: 'malayalam', label: 'Malayalam' },
  { key: 'urdu', label: 'Urdu' },
  { key: 'chinese', label: 'Chinese' },
  { key: 'portuguese', label: 'Portuguese' },
  { key: 'russian', label: 'Russian' },
];
const DEFAULT_SEARCH_LANGUAGES = ['english'];
const ANY_STATE_VALUE = '__ANY__';
const INDIA_STATE_OPTIONS = [
  'Goa', 'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Puducherry', 'Chandigarh',
];

const normalizeLlmDecision = (value) => {
  const raw = (value || '').toString().trim().toLowerCase();
  if (!raw) return 'unknown';
  if (raw.includes('likely_fraud') || raw === 'fraud' || raw === 'fraudulent') return 'fraud';
  if (raw.includes('likely_legit') || raw === 'genuine' || raw === 'legit') return 'genuine';
  if (raw.includes('uncertain')) return 'uncertain';
  return 'unknown';
};

const parseExternalLinks = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'number') return new Array(Math.max(0, value)).fill('link-item');
  if (typeof value !== 'string') return [];

  const trimmed = value.trim();
  if (!trimmed) return [];

  try {
    const parsed = JSON.parse(trimmed.replace(/'/g, '"'));
    if (Array.isArray(parsed)) return parsed;
  } catch {
    // Fall through to loose parsing for older saved reports.
  }

  if (trimmed.includes(',')) {
    return trimmed.split(',').map(link => link.trim()).filter(Boolean);
  }
  return /^https?:\/\//i.test(trimmed) ? [trimmed] : [];
};

const matchedKeywordHitCount = (value) => {
  if (Array.isArray(value)) return value.length;
  if (!value || typeof value !== 'object') return 0;

  return Object.values(value).reduce((sum, hitValue) => {
    if (Array.isArray(hitValue)) return sum + hitValue.length;
    const n = Number(hitValue);
    return sum + (Number.isFinite(n) ? n : 1);
  }, 0);
};

const truncateForDb = (value, limit = 500) => {
  const text = String(value || '').replace(/[\uD800-\uDFFF]/g, '').trim();
  return text.length <= limit ? text : `${text.slice(0, limit).trim()}...`;
};

const compactMatchedKeywordsForDb = (value, maxKeywords = 40) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};

  return Object.entries(value).slice(0, maxKeywords).reduce((acc, [keyword, hitValue]) => {
    if (Array.isArray(hitValue)) {
      acc[keyword] = hitValue.length;
      return acc;
    }
    const n = Number(hitValue);
    acc[keyword] = Number.isFinite(n) ? n : 1;
    return acc;
  }, {});
};

/*
// Evidence sample helpers kept for future use if evidence is moved to per-group docs/subcollections.
const compactEvidenceItemForDb = (item, maxChars = 500) => {
  if (!item || typeof item !== 'object' || Array.isArray(item)) {
    return truncateForDb(item, maxChars);
  }

  const keepKeys = [
    'matched_word', 'matchedWord', 'matched_phrase', 'matchedPhrase',
    'language', 'found_in', 'foundIn', 'message_text', 'messageText',
    'full_text', 'fullText', 'message_id', 'messageId', 'direct_link',
    'directLink', 'date'
  ];
  return keepKeys.reduce((acc, key) => {
    if (item[key] == null) return acc;
    if (['message_text', 'messageText', 'full_text', 'fullText'].includes(key)) {
      acc[key] = truncateForDb(item[key], maxChars);
    } else {
      acc[key] = item[key];
    }
    return acc;
  }, {});
};

const compactListForDb = (value, maxItems = 30, maxChars = 500) => {
  if (!Array.isArray(value)) return [];
  return value.slice(0, maxItems).map(item => compactEvidenceItemForDb(item, maxChars));
};

const compactMatchedKeywordEvidenceForDb = (value, maxTotalItems = 30) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};

  let remaining = maxTotalItems;
  return Object.entries(value).reduce((acc, [keyword, hitValue]) => {
    if (remaining <= 0 || !Array.isArray(hitValue) || hitValue.length === 0) return acc;
    const picked = hitValue.slice(0, remaining);
    acc[keyword] = picked.map(item => compactEvidenceItemForDb(item, 500));
    remaining -= picked.length;
    return acc;
  }, {});
};
*/

const getSignalDecisions = (group) => {
  const matchedKeywords = group?.matchedKeywords ?? group?.matched_keywords ?? {};
  const externalLinks = group?.externalLinks ?? group?.external_links ?? [];
  const keywordHits = matchedKeywordHitCount(matchedKeywords);
  const externalCount = parseExternalLinks(externalLinks).length;
  const keywordFlagged = keywordHits > 5;
  const externalFlagged = externalCount > 20;
  const backendKeywordLabel = group?.matchedKeywordLabel ?? group?.matched_keyword_label;
  const backendExternalLabel = group?.externalLinkLabel ?? group?.external_link_label;

  return {
    keywordStatus: backendKeywordLabel || (keywordFlagged ? 'fraud' : 'genuine'),
    externalStatus: backendExternalLabel || (externalFlagged ? 'fraud' : 'genuine'),
    keywordHits,
    externalCount,
    keywordFlagged,
    externalFlagged,
  };
};

const signalDisplay = (status) => {
  if (status === 'fraud') return 'Fraud';
  if (status === 'uncertain') return 'Uncertain';
  return 'Genuine';
};

const signalStyle = (status) => ({
  background:
    status === 'fraud' ? '#fff7ed' :
    status === 'uncertain' ? '#fffbeb' : '#f0fdf4',
  color:
    status === 'fraud' ? '#c2410c' :
    status === 'uncertain' ? '#b45309' : '#166534',
});

const llmConfidenceDisplay = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return n <= 1 ? `${Math.round(n * 100)}%` : `${Math.round(n)}%`;
};

const keywordRelatedValue = (group) => {
  const raw = group?.keywordRelated ?? group?.keyword_related;
  if (typeof raw === 'boolean') return raw;
  if (raw == null || raw === '') return null;
  const text = String(raw).trim().toLowerCase();
  if (['true', 'yes', 'y', '1', 'related', 'keyword_related'].includes(text)) return true;
  if (['false', 'no', 'n', '0', 'unrelated', 'not_related'].includes(text)) return false;
  return null;
};

const keywordRelatedRank = (group) => {
  const related = keywordRelatedValue(group);
  // Hard-prioritize topic relevance: unrelated groups must stay at the bottom
  // even if their fraud/uncertain score looks more urgent.
  if (related === true) return 2;
  if (related === null) return 1;
  return -1;
};

const keywordRelatedStyle = (related) => ({
  background: related ? '#ecfdf5' : '#fef2f2',
  color: related ? '#047857' : '#b91c1c',
});

const KeywordUnrelatedDivider = () => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      margin: '18px 0 10px',
      color: '#92400e',
      fontSize: 12,
      fontWeight: 800,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    }}
  >
    <span style={{ flex: 1, height: 1, background: '#fde68a' }} />
    <span
      style={{
        background: '#fffbeb',
        border: '1px solid #fde68a',
        borderRadius: 999,
        padding: '6px 12px',
      }}
    >
      Unrelated to searched keyword
    </span>
    <span style={{ flex: 1, height: 1, background: '#fde68a' }} />
  </div>
);

const aiBinaryDecisionFromLabel = (group) => {
  const normalized = normalizeLlmDecision(
    group?.llmDecision ?? group?.llm_decision ?? group?.llmLabel ?? group?.llm_label ?? ''
  );
  return normalized === 'fraud' || normalized === 'genuine' ? normalized : 'uncertain';
};

const aiBinaryDisplay = (decision) => {
  if (decision === 'fraud') return 'Fraud';
  if (decision === 'uncertain') return 'Uncertain';
  return 'Genuine';
};

const getDecisionLabels = (group) => {
  const signals = getSignalDecisions(group);
  const storedAiLabel = normalizeLlmDecision(group?.aiLabel ?? group?.ai_label ?? '');
  const inferredAiLabel = aiBinaryDecisionFromLabel(group);
  const titleText = (group?.groupTitle ?? group?.group_title ?? '').toString().trim().toLowerCase();
  const errorText = (group?.error ?? '').toString().trim().toLowerCase();
  const reasonText = (group?.llmReason ?? group?.llm_reason ?? '').toString().trim().toLowerCase();
  const hasInaccessiblePlaceholder =
    titleText === 'invalid private group' ||
    titleText === 'expired private group' ||
    errorText.includes('invalid invite') ||
    errorText.includes('invite link has expired') ||
    reasonText.includes('insufficient evidence');
  let aiLabel = storedAiLabel !== 'unknown' ? storedAiLabel : inferredAiLabel;
  if (hasInaccessiblePlaceholder && aiLabel !== 'fraud') {
    aiLabel = 'uncertain';
  }
  const matchedKeywordLabel = group?.matchedKeywordLabel ?? group?.matched_keyword_label ?? signals.keywordStatus;
  const externalLinkLabel = group?.externalLinkLabel ?? group?.external_link_label ?? signals.externalStatus;
  const ruleFraud = matchedKeywordLabel === 'fraud' && externalLinkLabel === 'fraud';
  const overallLabel =
    aiLabel === 'fraud' || ruleFraud
      ? 'fraud'
      : aiLabel === 'genuine' && matchedKeywordLabel === 'genuine' && externalLinkLabel === 'genuine'
        ? 'genuine'
        : 'uncertain';

  return {
    aiLabel,
    matchedKeywordLabel,
    externalLinkLabel,
    overallLabel,
    signals,
  };
};

const aiBadgeStyle = (decision) => ({
  background:
    decision === 'fraud' ? '#fef2f2' :
    decision === 'uncertain' ? '#fffbeb' : '#f0fdf4',
  color:
    decision === 'fraud' ? '#dc2626' :
    decision === 'uncertain' ? '#b45309' : '#166534',
});

function SignalReasonStrip({ signals }) {
  return (
    <div style={{
      marginTop: 10,
      padding: '8px 10px',
      background: '#f8fafc',
      border: '1px solid #e2e8f0',
      borderRadius: 6,
      fontSize: 12,
      color: '#475569',
      lineHeight: 1.45
    }}>
      <strong style={{ color: '#0f172a' }}>Signal check:</strong>{' '}
      External links are <strong style={{ color: signals.externalFlagged ? '#c2410c' : '#166534' }}>
        {signals.externalCount}
      </strong>{' '}
      {signals.externalFlagged ? 'which is above the 20-link fraud threshold' : 'which stays within the safe 20-link limit'}.
      {' '}Matched keyword hits are <strong style={{ color: signals.keywordFlagged ? '#c2410c' : '#166534' }}>
        {signals.keywordHits}
      </strong>{' '}
      {signals.keywordFlagged ? 'which is above the 5-hit fraud threshold' : 'which stays within the safe 5-hit limit'}.
    </div>
  );
}

/**
 * ── EXPIRED-GROUP FILTER ─────────────────────────────────────
 * Called before building the combined list.
 * Returns false for any group that could not be accessed:
 *   • has an error string (invite expired, entity_failed, no user…)
 *   • title is literally "Expired Private Group"
 *   • zero hits + zero score + no matched keywords  → no signal
 */
const isValidDiscoveryGroup = (g) => {
  // If there's any form of hit, we must show it!
  const hits = Number(g.total_hits || g.totalHits || 0);
  const phits = Number(g.phrase_hits || g.phraseHits || 0);
  if (hits > 0 || phits > 0) return true;

  const t = (g.group_title || g.groupName || g.username || '').toLowerCase();
  if (t === 'expired private group')             return false;   // known expired sentinel
  if (t.includes('expired') && !g.member_count)  return false;   // probable expired invite
  return true;
};

/* ── hash for dedup ─────────────────────────────────────────── */
const hashKey = (value) => {
  const s = String(value || '');
  let h1 = 0xdeadbeef, h2 = 0x41c6ce57;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 2654435761);
    h2 = Math.imul(h2 ^ c, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return `${(h2 >>> 0).toString(36)}${(h1 >>> 0).toString(36)}`;
};

const getActiveUnifiedScanStorageKey = (user) => `active_unified_scan:${user?.uid || 'anonymous'}`;

/* ── Group Reviews helpers ──────────────────────────────────── */
const normalizeGroupIdentifier = (identifier) =>
  identifier
    ?.replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/+$/, '')
    .trim()
    .toLowerCase() || '';

const getReviewGroupKey = (group) => {
  const normalizedLink = normalizeGroupIdentifier(group?.groupLink || group?.group_link || group?.link || '');
  if (normalizedLink) return `link:${normalizedLink}`;

  const normalizedName = normalizeGroupIdentifier(
    group?.groupName || group?.group_info?.title || group?.group_username || group?.username || group?.group_title || ''
  );
  return normalizedName ? `name:${normalizedName}` : '';
};

const getReviewGroupIdentity = (group) => ({
  groupLink:
    group?.groupLink ||
    group?.group_link ||
    group?.link ||
    ((group?.username || group?.group_username) ? `https://t.me/${group?.username || group?.group_username}` : ''),
  groupName:
    group?.groupName ||
    group?.group_info?.title ||
    group?.group_username ||
    group?.username ||
    group?.group_title ||
    'Unknown Group',
});

const getEmbeddedReviewFromGroup = (group) => {
  const status = group?.reviewStatus;
  if (status !== 'fake' && status !== 'suspicious' && status !== 'genuine') return null;
  return {
    status,
    reason: group?.reviewReason || '',
    officerId: group?.reviewedBy || '',
    blockedStatus: group?.blockedStatus || (status === 'fake' ? 'not_blocked' : null),
    timestamp: group?.reviewedAt || null,
    timestampMs: Number(group?.reviewedAtMs) || 0,
    groupLink: group?.groupLink || group?.group_link || group?.link || '',
    groupName: group?.groupName || group?.group_title || group?.username || 'Unknown Group',
  };
};

const csvEscape = (value) => {
  const text = value == null ? '' : String(value);
  return `"${text.replace(/"/g, '""')}"`;
};

const csvList = (value) => {
  if (!value) return '';
  if (Array.isArray(value)) return value.filter(Boolean).join('; ');
  if (typeof value === 'object') return Object.entries(value)
    .map(([key, val]) => `${key}:${Array.isArray(val) ? val.length : val}`)
    .join('; ');
  return String(value);
};

const buildTelegramGroupsCsv = (groups, reviewsMap = {}) => {
  const headers = [
    'Sr No',
    'Group Name',
    'Group Link',
    'Username',
    'Review Status',
    'Overall Decision',
    'AI Decision',
    'AI Confidence',
    'Keyword Related',
    'Keyword Related Reason',
    'Matched Keywords Status',
    'External Links Status',
    'Total Hits',
    'Phrase Hits',
    'Matched Keyword Hits',
    'External Links Count',
    'Members',
    'Admins',
    'Public',
    'Verified',
    'Location Relevant',
    'Discovery Engines',
    'LLM Reason',
    'Description',
  ];

  const rows = groups.map((group, index) => {
    const labels = getDecisionLabels(group);
    const review = reviewsMap[getReviewGroupKey(group)] || getEmbeddedReviewFromGroup(group);
    const signals = labels.signals;
    const keywordRelated = keywordRelatedValue(group);
    const externalLinksRaw =
      group.externalLinksTotal ??
      group.external_links_total ??
      group.externalLinks ??
      group.external_links;
    const externalLinksCount = Array.isArray(externalLinksRaw)
      ? externalLinksRaw.length
      : (Number(externalLinksRaw) || parseExternalLinks(externalLinksRaw).length || 0);

    const row = [
      index + 1,
      group.groupName || group.group_title || group.username || 'Unknown',
      group.groupLink || group.group_link || group.link || '',
      group.username || group.group_username || '',
      review?.status || group.reviewStatus || 'unreviewed',
      signalDisplay(labels.overallLabel),
      aiBinaryDisplay(labels.aiLabel),
      llmConfidenceDisplay(group.llmConfidence ?? group.llm_confidence),
      keywordRelated === null ? 'Unknown' : (keywordRelated ? 'Yes' : 'No'),
      group.keywordRelatedReason || group.keyword_related_reason || '',
      signalDisplay(labels.matchedKeywordLabel),
      signalDisplay(labels.externalLinkLabel),
      group.totalHits ?? group.total_hits ?? 0,
      group.phraseHits ?? group.phrase_hits ?? 0,
      signals.keywordHits,
      externalLinksCount,
      group.memberCount ?? group.member_count ?? group.group_info?.member_count ?? '',
      group.adminCount ?? group.admin_count ?? '',
      group.isPublic ?? group.is_public ?? group.group_info?.is_public ?? '',
      group.isVerified ?? group.is_verified ?? false,
      group.locationRelevant ?? group.location_relevant ?? group.goaRelevant ?? group.goa_relevant ?? false,
    csvList(group.discoveredByEngines || group.discovered_by_engines || []),
      group.llmReason || group.llm_reason || '',
      group.description || group.group_description || '',
    ];

    return row.map(csvEscape).join(',');
  });

  return [headers.map(csvEscape).join(','), ...rows].join('\r\n');
};

const downloadCsvFile = (filename, csvText) => {
  const blob = new Blob([`\uFEFF${csvText}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const getReviewTimestampMs = (review) => {
  const direct = Number(review?.timestampMs);
  if (Number.isFinite(direct) && direct > 0) return direct;
  const parsed = review?.timestamp ? new Date(review.timestamp).getTime() : 0;
  return Number.isFinite(parsed) ? parsed : 0;
};

const upsertLatestReview = (reviewsMap, review) => {
  const keys = [];
  if (review?.groupLink) keys.push(`link:${normalizeGroupIdentifier(review.groupLink)}`);
  if (review?.groupName) keys.push(`name:${normalizeGroupIdentifier(review.groupName)}`);

  keys.filter(Boolean).forEach((key) => {
    const existing = reviewsMap[key];
    if (!existing || getReviewTimestampMs(review) >= getReviewTimestampMs(existing)) {
      reviewsMap[key] = review;
    }
  });
};

const buildLocationLabel = (target = {}) => {
  const city = String(target?.city || '').trim();
  const state = String(target?.state || '').trim();
  return [city, state].filter(Boolean).join(', ');
};

const buildLocationPartsForMode = (locationTarget = {}, lang = "english") => {
  const rawCity = String(locationTarget?.city || '').trim();
  const rawState = String(locationTarget?.state || '').trim();
  const mode = String(locationTarget?.queryMode || 'both').trim() || 'both';

  const translatedCity = String(
    locationTarget?.translated_city?.[lang] ||
    locationTarget?.translated_city?.english ||
    rawCity
  ).trim();
  const translatedState = String(
    locationTarget?.translated_state?.[lang] ||
    locationTarget?.translated_state?.english ||
    rawState
  ).trim();

  if (mode === 'city_only') return translatedCity ? [translatedCity] : [];
  if (mode === 'state_only') return translatedState ? [translatedState] : [];

  const parts = [];
  if (translatedCity) parts.push(translatedCity);
  if (translatedState) parts.push(translatedState);
  return parts;
};

const buildSearchQueriesUsed = (translations = {}, locationTarget = {}) => {
  const queries = [];

  Object.values(translations || {}).forEach((perPhrase) => {
    if (!perPhrase || typeof perPhrase !== 'object') return;
    Object.entries(perPhrase).forEach(([lang, translatedPhrase]) => {
      const phrase = String(translatedPhrase || '').trim();
      if (!phrase) return;

      const locationParts = buildLocationPartsForMode(locationTarget, lang);

      const fullQuery = [phrase, locationParts.join(', ')].filter(Boolean).join(' ').trim();
      if (fullQuery) queries.push(fullQuery);
    });
  });

  return [...new Set(queries)];
};

const PHASE_LABELS = {
  initializing:       'Initializing...',
  translating:        'Translating keywords...',
  discovering_groups: 'Discovering groups...',
  preparing_scan:     'Preparing scan...',
  scanning_groups:    'Scanning groups (hybrid)...',
  generating_report:  'Generating report...',
  resuming:           'Resuming search...',
};
const PHASE_STEPS  = ['translating', 'discovering_groups', 'scanning_groups', 'generating_report'];
const PHASE_LABEL  = ['1. Translate', '2. Discover', '3. Scan', '4. Report'];
const ALL_PHASES   = ['translating', 'discovering_groups', 'preparing_scan', 'scanning_groups', 'generating_report'];

/* ══════════════════════════════════════════════════════════════ */
/* UnifiedGroupCard                                               */
/* ══════════════════════════════════════════════════════════════ */
function UnifiedGroupCard({ g, review, onReview, onBlockedStatusChange, index }) {
  const [showLinks, setShowLinks] = useState(false);
  const [showPhrases, setShowPhrases] = useState(false);
  const [showGoaMatches, setShowGoaMatches] = useState(false);
  const [showPhoneMatches, setShowPhoneMatches] = useState(false);
  
  // Property normalization (camelCase from DB vs snake_case from Backend)
  const total_hits      = g.totalHits ?? g.total_hits ?? 0;
  const phrase_hits     = g.phraseHits ?? g.phrase_hits ?? 0;
  const phrase_matches  = g.phraseMatches ?? g.phrase_matches ?? [];
  const external_links  = g.externalLinkItems ?? g.external_link_items ?? g.externalLinks ?? g.external_links ?? [];
  const extracted_phones = g.extracted_phone_numbers ?? g.extractedPhoneNumbers ?? [];
  const phone_matches   = g.phone_number_matches ?? g.phoneNumberMatches ?? [];

  // Highlighter for keywords
  const highlightKeywords = (text, phrase) => {
    if (!phrase || !text) return text;
    const words = phrase.split(',').map(w => w.trim()).filter(Boolean);
    if (words.length === 0) return text;
    
    let highlightedText = text;
    // Sort words by length descending to avoid partial matches on shorter words first
    words.sort((a, b) => b.length - a.length).forEach(word => {
      const regex = new RegExp(`(${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      highlightedText = highlightedText.replace(regex, '<strong style="background: #fef08a; padding: 0 2px; border-radius: 2px;">$1</strong>');
    });
    
    return <span dangerouslySetInnerHTML={{ __html: highlightedText }} />;
  };

  // External Links parsing
  const linksArray = parseExternalLinks(external_links);
  const linkSamples = linksArray.filter(u => /^https?:\/\//i.test(String(u)));
  const externalLinksTotal = Number(
    g.externalLinksTotal ??
    g.external_links_total ??
    (Array.isArray(g.external_links) ? g.external_links.length : g.externalLinks)
  ) || linksArray.length;
  const phraseMatchesTotal = Number(g.phraseMatchesTotal ?? g.phrase_matches_total) || phrase_matches.length;

  // Use backend provided values or fall back to calculation
  const score  = g.score || (Number(total_hits) + Number(phrase_hits) + externalLinksTotal);
  const rLevel = g.risk_level || hitsRiskLevel(score);
  
  const link   = g.groupLink || g.group_link || g.link || (g.username ? `https://t.me/${g.username}` : '');
  const title  = g.groupName || g.group_title || g.username || link.split('/').pop() || 'Unknown';
  const members = Number(g.memberCount ?? g.member_count ?? (g.group_info?.member_count)) || null;
  const isPublic = g.isPublic ?? g.is_public ?? (g.group_info?.is_public ?? null);
  
  // AI Note / Reason normalization
  const llmNote = g.llmReason ?? g.llm_reason ?? '';
  const llmConfidence = llmConfidenceDisplay(g.llmConfidence ?? g.llm_confidence);
  const keywordRelated = keywordRelatedValue(g);
  const keywordRelatedReason = g.keywordRelatedReason ?? g.keyword_related_reason ?? '';
  const decisionLabels = getDecisionLabels(g);
  const signalDecision = decisionLabels.signals;

  // New fields: Description, Admin Info, Photos, Last Active
  const description = g.description ?? g.group_description ?? '';
  const adminCount = g.adminCount ?? g.admin_count ?? null;
  const hasPhoto = g.hasPhoto ?? g.has_photo ?? null;
  const lastActive = g.lastActiveDate ?? g.last_active_date ?? g.lastMessageDate ?? g.last_message_date ?? g.group_info?.last_active_date ?? null;

  const locationRelevant = g.locationRelevant ?? g.location_relevant ?? g.goaRelevant ?? g.goa_relevant ?? false;
  const locationMatches  = g.locationMatches ?? g.location_matches ?? g.goaMatches ?? g.goa_matches ?? [];
  const locationLabel = buildLocationLabel(g.locationTarget ?? g.location_target) || 'Location Focus';
  const discoveredByEngines = g.discoveredByEngines ?? g.discovered_by_engines ?? [];

  return (
    <div className="list-item" style={{ borderLeft: `4px solid ${riskColour(rLevel)}` }}>
      <div className="discover-item-body">
        {/* ── Title Row ── */}
        <div className="list-title discover-item-title">
          <span style={{ color: '#94a3b8', fontWeight: 600, marginRight: 4 }}>{index}.</span>
          {title}
          
          {/* Public / Private Badge */}
          {isPublic !== null && (
            <span className="tag" style={{ 
              background: isPublic ? '#eff6ff' : '#f1f5f9', 
              color: isPublic ? '#1d4ed8' : '#64748b', 
              fontSize: '0.75rem',
              fontWeight: 600
            }}>
              {isPublic ? 'Public' : 'Private'}
            </span>
          )}

          {g.is_verified && <span className="tag tag-success">✓ Verified</span>}
          <span className="tag" style={{
            ...signalStyle(decisionLabels.overallLabel),
            fontSize: '0.75rem',
            fontWeight: 800
          }}>
            Overall:{signalDisplay(decisionLabels.overallLabel)}
          </span>
          {/* Location Badge */}
          {locationRelevant && (
            <span className="tag" style={{
              background: '#fef3c7', color: '#92400e',
              fontSize: '0.75rem', fontWeight: 700
            }}>
              📍 {locationLabel}
            </span>
          )}

          {/* Last Active Tag pushed to the right */}
          {lastActive && (
            <span className="tag" style={{
              marginLeft: 'auto',
              background: '#e2e8f0', color: '#334155',
              fontSize: '0.75rem', fontWeight: 500
            }}>
              Last Active: {new Date(lastActive).toLocaleDateString()}
            </span>
          )}
        </div>

        {/* ── Link ── */}
        <div className="list-sub discover-item-link">
          <a href={link} target="_blank" rel="noreferrer">{link}</a>
        </div>

        {/* ── Meta Grid ── */}
        <div className="discover-item-meta-grid">
          <div><strong>Members:</strong> {members ? members.toLocaleString() : 'N/A'}</div>
          {adminCount !== null && adminCount !== undefined && (
            <div><strong>Admins:</strong> {adminCount.toLocaleString()}</div>
          )}
          <div><strong>Photos:</strong> {hasPhoto === true ? 'Yes' : (hasPhoto === false ? 'No' : 'N/A')}</div>
          <div><strong>Total Hits:</strong> {total_hits}</div>
          <div><strong>Evidence Hits:</strong> {phrase_hits}</div>
          <div><strong>External Links:</strong> {externalLinksTotal}</div>
          <div><strong>Matched Keywords:</strong> {signalDecision.keywordHits}</div>
          <div><strong>Phone Numbers:</strong> <span style={{ color: extracted_phones.length > 0 ? '#15803d' : '#475569', fontWeight: extracted_phones.length > 0 ? 700 : 400 }}>{extracted_phones.length}</span></div>
        </div>

        {/* ── Engine Source ── */}
        <div className="chips" style={{ gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
          <span className="tag" title={`LLM confidence: ${llmConfidence || 'N/A'}`} style={{ ...aiBadgeStyle(decisionLabels.aiLabel), fontSize: '0.75rem', fontWeight: 700 }}>
            AI:{aiBinaryDisplay(decisionLabels.aiLabel)}{llmConfidence ? ` ${llmConfidence}` : ''}
          </span>
          {keywordRelated !== null && (
            <span
              className="tag"
              title={keywordRelatedReason || (keywordRelated ? 'Matches the search topic' : 'Does not match the search topic')}
              style={{ ...keywordRelatedStyle(keywordRelated), fontSize: '0.75rem', fontWeight: 700 }}
            >
              Keyword Related:{keywordRelated ? 'Yes' : 'No'}
            </span>
          )}
          <span className="tag" title={`Matched keyword hits: ${signalDecision.keywordHits}/5`} style={{ ...signalStyle(decisionLabels.matchedKeywordLabel), fontSize: '0.75rem', fontWeight: 700 }}>
            Matched Keywords:{signalDisplay(decisionLabels.matchedKeywordLabel)}
          </span>
          <span className="tag" title={`External links: ${signalDecision.externalCount}/20`} style={{ ...signalStyle(decisionLabels.externalLinkLabel), fontSize: '0.75rem', fontWeight: 700 }}>
            External Links:{signalDisplay(decisionLabels.externalLinkLabel)}
          </span>
          {extracted_phones.length > 0 && (
            <span className="tag" title={`Extracted Phone Numbers: ${extracted_phones.length}`} style={{ background: '#dcfce7', color: '#15803d', border: '1px solid #86efac', fontSize: '0.75rem', fontWeight: 700 }}>
              📞 Phone Numbers: {extracted_phones.length}
            </span>
          )}
        </div>

        <SignalReasonStrip signals={signalDecision} />

        {discoveredByEngines.length > 0 && (
          <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>
            <strong>S-engine:</strong>{' '}
            {discoveredByEngines.map((eng, i) => (
              <span key={eng} className="tag" style={{
                background: '#e0f2fe', color: '#0369a1',
                fontSize: '0.7rem', fontWeight: 600,
                marginRight: 4, padding: '2px 6px'
              }}>
                {eng.charAt(0).toUpperCase() + eng.slice(1)}
              </span>
            ))}
          </div>
        )}

        {/* ── Group Description ── */}
        {description && (
          <div className="discover-item-block discover-item-text" style={{ 
            marginTop: 10, 
            padding: '10px 12px', 
            background: '#f8fafc', 
            border: '1px solid #e2e8f0', 
            borderRadius: '6px',
            maxHeight: '120px',
            overflowY: 'auto'
          }}>
            <strong style={{ color: '#475569', fontSize: '11px', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>
              Description
            </strong>
            <div style={{ fontSize: '12.5px', color: '#334155', whiteSpace: 'pre-wrap', lineHeight: '1.4' }}>
              {description}
            </div>
          </div>
        )}

        {/* ── Extracted Phone Numbers ── */}
        {extracted_phones.length > 0 && (
          <div className="discover-item-block discover-item-text" style={{ 
            marginTop: 10, 
            padding: '10px 12px', 
            background: '#f0fdf4', 
            border: '1px solid #bbf7d0', 
            borderRadius: '6px'
          }}>
            <strong style={{ color: '#166534', fontSize: '11px', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
              📱 Contact Numbers Found ({extracted_phones.length})
            </strong>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {extracted_phones.map(p => (
                <a key={p} href={`tel:${p}`} style={{
                  background: '#ffffff', color: '#15803d', border: '1px solid #86efac',
                  borderRadius: '4px', padding: '2px 8px', fontSize: '12px', fontWeight: 700,
                  textDecoration: 'none'
                }}>
                  {p}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* ── Phone Number Matches (Direct Message Links) ── */}
        {phone_matches?.length > 0 && (
          <div className="discover-item-block" style={{ marginTop: 8 }}>
            <button
              type="button"
              className="ghost-btn"
              onClick={() => setShowPhoneMatches(o => !o)}
              style={{ fontSize: 12, padding: '4px 0', color: '#15803d', fontWeight: 700 }}
            >
              {showPhoneMatches ? '▲ Hide Phone Matches' : `▼ View ${phone_matches.length} Phone Number Message Contexts`}
            </button>
            {showPhoneMatches && (
              <div className="phrase-match-list" style={{ marginTop: 8 }}>
                {phone_matches.map((m, i) => (
                  <div key={i} className="phrase-match-card" style={{ 
                    background: '#f0fdf4', 
                    padding: '10px 12px', 
                    marginBottom: 8, 
                    borderRadius: '8px',
                    border: '1px solid #bbf7d0'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4 }}>
                      <span className="tag" style={{ background: '#dcfce7', color: '#15803d', fontWeight: 700 }}>
                        📍 {m.found_in || 'message_scan'}
                      </span>
                      {m.direct_link && (
                        <a href={m.direct_link} target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontWeight: 600 }}>
                          Open Message ↗
                        </a>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: '#166534', fontWeight: 700 }}>
                      Phones: {(m.phone_numbers || []).join(', ')}
                    </div>
                    {m.message_text && (
                      <div style={{ fontSize: 12, color: '#334155', marginTop: 4, whiteSpace: 'pre-wrap' }}>
                        {m.message_text}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Matched Phrases ── */}
        {phrase_matches?.length > 0 && (
          <div className="discover-item-block">
            <button
              type="button"
              className="ghost-btn"
              onClick={() => setShowPhrases(o => !o)}
              style={{ fontSize: 12, padding: '4px 0', color: '#2563eb', fontWeight: 600 }}
            >
              {showPhrases ? '▲ Hide Evidence Context' : `▼ View ${phrase_matches.length} of ${phraseMatchesTotal} Keyword Context Hits`}
            </button>
            {showPhrases && (
              <div className="phrase-match-list" style={{ marginTop: 10 }}>
                {phrase_matches.map((m, i) => (
                  <div key={i} className="phrase-match-card" style={{ 
                    background: '#f8fafc', 
                    padding: '12px', 
                    marginBottom: 10, 
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    position: 'relative'
                  }}>
                    <div className="phrase-language-row" style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      fontSize: 11, 
                      marginBottom: 6 
                    }}>
                      <div>
                        <span className="tag" style={{ background: '#e0f2fe', color: '#0369a1', fontWeight: 600 }}>
                          {langFlag(m.language)} {m.language?.toUpperCase()}
                        </span>
                        {m.found_in && (
                          <span className="tag" style={{ background: '#fef3c7', color: '#92400e', marginLeft: 6, fontWeight: 700 }}>
                            📍 {m.found_in}
                          </span>
                        )}
                        <span style={{ marginLeft: 8, color: '#475569' }}>
                          Hit: <strong style={{ color: '#0f172a' }}>"{m.matched_phrase || m.matched_word || 'Keyword'}"</strong>
                        </span>
                      </div>
                      {m.date && m.date !== 'N/A' && (
                        <span style={{ color: '#94a3b8' }}>
                          {new Date(m.date).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                        </span>
                      )}
                    </div>
                    <div style={{ 
                      fontSize: '13px', 
                      lineHeight: '1.5', 
                      color: '#1e293b', 
                      background: '#ffffff',
                      padding: '8px 10px',
                      borderRadius: '6px',
                      borderLeft: '3px solid #cbd5e1',
                      marginBottom: 8
                    }}>
                      {highlightKeywords(m.message_text, m.matched_phrase)}
                    </div>
                    {m.direct_link && (
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <a 
                          href={m.direct_link} 
                          target="_blank" 
                          rel="noreferrer" 
                          style={{ 
                            fontSize: 11, 
                            color: '#2563eb', 
                            textDecoration: 'none', 
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            background: '#eff6ff',
                            padding: '4px 8px',
                            borderRadius: '4px'
                          }}
                        >
                          <span>🔗 VIEW IN TELEGRAM</span>
                        </a>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── AI Note ── */}
        {llmNote && (
          <div className="discover-item-block discover-item-text" style={{ 
            marginTop: 12, 
            padding: '12px', 
            background: '#f0fdf4', 
            border: '1px solid #bbf7d0', 
            borderRadius: '8px',
            fontSize: '13.5px',
            lineHeight: '1.5',
            color: '#166534'
          }}>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '4px' }}>
              <strong style={{ color: '#166534', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.05em' }}>AI Intelligence Note</strong>
            </div>
            {llmNote}
          </div>
        )}

        {/* ── Location Match Evidence ── */}
        {locationRelevant && locationMatches.length > 0 && (
          <div className="discover-item-block">
            <button
              type="button"
              className="ghost-btn"
              onClick={() => setShowGoaMatches(o => !o)}
              style={{ fontSize: 12, padding: '2px 0' }}
            >
              {showGoaMatches ? `▲ Hide ${locationLabel} matches` : `▼ View ${locationMatches.length} ${locationLabel} match${locationMatches.length > 1 ? 'es' : ''}`}
            </button>
            {showGoaMatches && (
              <div style={{ marginTop: 8 }}>
                {locationMatches.map((m, i) => (
                  <div key={i} style={{
                    background: '#fffbeb', padding: 10, marginBottom: 6,
                    borderRadius: 6, border: '1px solid #fde68a', fontSize: 12.5
                  }}>
                    <div style={{ marginBottom: 4 }}>
                      <span className="tag" style={{ background: '#fef3c7', color: '#92400e', fontWeight: 700 }}>
                        📍 {(m.matched_word || m.matchedWord || '').charAt(0).toUpperCase() + (m.matched_word || m.matchedWord || '').slice(1)}
                      </span>
                      <span className="tag" style={{ background: '#e2e8f0', marginLeft: 4 }}>
                        {(m.language || '').toUpperCase()}
                      </span>
                      <span style={{ marginLeft: 8, color: '#64748b' }}>
                        Found in: <strong>{(m.found_in || m.foundIn || '').replace(/_/g, ' ')}</strong>
                      </span>
                    </div>
                    {(m.full_text || m.fullText) && (
                      <div style={{
                        fontSize: 12, color: '#334155', lineHeight: 1.4,
                        whiteSpace: 'pre-wrap', maxHeight: 80, overflowY: 'auto',
                        background: '#fff', padding: 6, borderRadius: 4, marginTop: 4
                      }}>
                        {m.full_text || m.fullText}
                      </div>
                    )}
                    {(m.direct_link || m.directLink) && (
                      <a href={m.direct_link || m.directLink} target="_blank" rel="noreferrer"
                        style={{ fontSize: 11, color: '#2563eb', textDecoration: 'none', fontWeight: 600, marginTop: 4, display: 'inline-block' }}>
                        🔗 View Message
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── External Links ── */}
        {linkSamples.length > 0 && (
          <div className="discover-item-block discover-item-text">
            <strong>External Links ({linkSamples.length} of {externalLinksTotal}):</strong>
            <div className="discover-links-list">
              {linkSamples.slice(0, showLinks ? linkSamples.length : 3).map((u, i) => (
                <a key={i} href={u} target="_blank" rel="noreferrer" className="discover-link-anchor" style={{ display: 'block', textDecoration: 'none', color: '#475569', fontSize: 12 }}>
                  {u.length > 70 ? u.slice(0, 70) + '…' : u}
                </a>
              ))}
              {linkSamples.length > 3 && (
                <span className="discover-link-toggle" onClick={() => setShowLinks(s => !s)} style={{ fontSize: 12, cursor: 'pointer', color: '#2563eb' }}>
                  {showLinks ? 'Read less' : `Read more (+${linkSamples.length - 3})`}
                </span>
              )}
            </div>
          </div>
        )}

        {/* ── Review Actions ── */}
        <div style={{ marginTop: 12, borderTop: '1px solid #f1f5f9', paddingTop: 12 }}>
          {!review ? (
            <div className="phrase-review-actions" style={{ display: 'flex', gap: '8px' }}>
              <button className="action-btn btn-danger" onClick={() => onReview(g, 'fake')}>
                Mark Fraudulent
              </button>
              <button
                className="action-btn"
                onClick={() => onReview(g, 'suspicious')}
                style={{ background: '#f59e0b', color: '#fff', borderColor: '#f59e0b' }}
              >
                Mark Suspicious
              </button>
              <button className="action-btn btn-success" onClick={() => onReview(g, 'genuine')}>
                Mark Genuine
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13 }}>
              <div className="phrase-reviewed-summary" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <span
                  className={`tag ${review.status === 'fake' ? 'tag-high' : review.status === 'suspicious' ? '' : 'tag-success'}`}
                  style={review.status === 'suspicious' ? { background: '#fef3c7', color: '#92400e', fontWeight: 700 } : undefined}
                >
                  {review.status === 'fake' ? 'Fraudulent' : review.status === 'suspicious' ? 'Suspicious' : 'Genuine'}
                </span>
                <span className="tag" style={{ background: '#e2e8f0', color: '#475569' }}>
                  Reviewed by: {review.officerId || 'Unknown'}
                </span>
                {review.reason && (
                  <span className="tag" style={{ background: '#e2e8f0', color: '#475569' }}>
                    Reason: {review.reason}
                  </span>
                )}
                {review.status === 'fake' && (
                  <span className="tag" style={{
                    background: review.blockedStatus === 'blocked' ? '#fee2e2' : '#dbeafe',
                    color: review.blockedStatus === 'blocked' ? '#991b1b' : '#1e3a8a',
                    fontWeight: 700
                  }}>
                    {review.blockedStatus === 'blocked' ? 'Blocked' : 'Not Blocked'}
                  </span>
                )}
              </div>
              {review.status === 'fake' && review.blockedStatus !== 'blocked' && (
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="action-btn btn-danger"
                    onClick={() => onBlockedStatusChange(g, 'blocked')}
                    style={{ minWidth: 140 }}
                  >
                    Mark Blocked
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/* Main Page                                                      */
/* ══════════════════════════════════════════════════════════════ */
export default function TelegramSearch() {
  const user = auth.currentUser;
  const activeUnifiedScanStorageKey = getActiveUnifiedScanStorageKey(user);

  /* ── AI Gen state ── */
  const [aiContext, setAiContext] = useState('');
  const [generatedPhraseBatches, setGeneratedPhraseBatches] = useState([]);
  const [selectedPhrases, setSelectedPhrases] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingHashed, setIsGeneratingHashed] = useState(false);

  /* ── form state ── */
  const [keyword,   setKeyword]   = useState('');
  const [selectedLanguages, setSelectedLanguages] = useState(DEFAULT_SEARCH_LANGUAGES);
  const [selectedState, setSelectedState] = useState('Goa');
  const [selectedCity, setSelectedCity] = useState('');
  const [queryLocationMode, setQueryLocationMode] = useState('state_only');
  const maxGroups = 130;
  const msgLimit  = 500;

  /* ── Group Reviews ── */
  const [groupReviews, setGroupReviews] = useState({});

  /* ── scan state ── */
  const [scanning,    setScanning]    = useState(false);
  const [operationId, setOperationId] = useState(null);
  const [scanProgress, setScanProgress] = useState(null);
  const [error,       setError]       = useState(null);
  const [scanNotice,  setScanNotice]  = useState(null);

  /* ── results ── */
  const [results,      setResults]      = useState(null);
  const [savedReportId, setSavedReportId] = useState('');
  const savingRef   = useRef(false);
  const savedKeyRef = useRef(null);

  /* ── history state ── */
  const [showHistory, setShowHistory] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [currentHistoryPage, setCurrentHistoryPage] = useState(0);
  const [historyHasMoreByPage, setHistoryHasMoreByPage] = useState({});

  /* ── filters ── */
  const [locationFilter, setLocationFilter] = useState('focused');
  const [overallFilter, setOverallFilter] = useState('All');
  const [secondarySort, setSecondarySort] = useState('recent_active');

  /* ── api status ── */
  const [apiOnline, setApiOnline] = useState(null);
  const [apiStatus, setApiStatus] = useState('checking');
  const isAnyStateSearch = selectedState === ANY_STATE_VALUE;

  const progressPct = deriveProgressPercent(scanProgress);
  const generatedPhrases = useMemo(
    () => generatedPhraseBatches.flatMap((batch) => batch.phrases || []),
    [generatedPhraseBatches]
  );
  const latestGeneratedBatch = useMemo(
    () => generatedPhraseBatches[generatedPhraseBatches.length - 1] || null,
    [generatedPhraseBatches]
  );

  /* ── API health check ── */
  useEffect(() => {
    const check = async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_API_BASE_URL}/scan-reports?limit=1`, {
          headers: { 'X-API-Key': process.env.REACT_APP_API_KEY }
        });
        setApiOnline(r.ok);
        setApiStatus(r.ok ? 'available' : 'unavailable');
      } catch {
        setApiOnline(false);
        setApiStatus('unavailable');
      }
    };
    check();
    // No idle polling; keep health check one-time on mount.
  }, []);

  /* ── Load Group Reviews ── */
  useEffect(() => {
    let unsubscribe = null;
    if (apiStatus === 'checking') return;

    const loadGroupReviews = async () => {
      if (apiStatus === 'available') {
        try {
          const response = await groupReviewsService.getReviews();
          const reviews = response.reviews || response.data || (Array.isArray(response) ? response : []);
          const reviewsMap = {};
          reviews.forEach((review) => {
            upsertLatestReview(reviewsMap, review);
          });
          setGroupReviews(reviewsMap);
        } catch (error) {
          console.warn('Backend API review fetch failed, falling back to Firebase:', error);
          loadGroupReviewsFromFirebase();
        }
      } else {
        loadGroupReviewsFromFirebase();
      }
    };

    const loadGroupReviewsFromFirebase = () => {
      unsubscribe = onSnapshot(collection(db, 'group_reviews'), (snap) => {
        if (apiStatus === 'available') {
          setGroupReviews((prev) => {
            const merged = { ...prev };
            snap.forEach((d) => {
              const data = d.data();
              upsertLatestReview(merged, data);
            });
            return merged;
          });
          return;
        }

        const reviewsMap = {};
        snap.forEach((d) => {
          const data = d.data();
          upsertLatestReview(reviewsMap, data);
        });
        setGroupReviews(reviewsMap);
      });
    };

    loadGroupReviews();
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [apiStatus]);

  /* ── save to DB (called ONLY on scan completion) ── */
  const saveToDb = useCallback(async (data, opId) => {
    if (savingRef.current) return;
    const pgs = data.phrase_match_groups || [];
    const dgs = data.discovery_groups    || [];
    const rawOpId = (opId || '').replace(/^unified_/, '');
    const fp = hashKey(`${data.keyword_phrase}|${pgs.length}|${dgs.length}|${data.scan_timestamp}`);
    if (savedKeyRef.current === fp) return;
    savingRef.current = true;
    
    const validDgs = dgs.filter(isValidDiscoveryGroup);
    if (validDgs.length === 0) {
      console.log('[UnifiedSave] Skipping save: No valid groups found in this scan.');
      savingRef.current = false;
      return;
    }
    
    const docId = rawOpId ? `unified_${rawOpId}` : `unified_${hashKey(fp)}`;
    
    const groupsDetails = validDgs.map(g => {
      const rawExternalLinks = parseExternalLinks(g.external_links ?? g.externalLinks);
      const rawPhraseMatches = Array.isArray(g.phrase_matches ?? g.phraseMatches) ? (g.phrase_matches ?? g.phraseMatches) : [];
      const rawLocationMatches = Array.isArray(g.location_matches ?? g.locationMatches) ? (g.location_matches ?? g.locationMatches) : [];
      const rawGoaMatches = Array.isArray(g.goa_matches ?? g.goaMatches) ? (g.goa_matches ?? g.goaMatches) : [];
      const rawMatchedKeywords = g.matched_keywords || {};
      const extCount = rawExternalLinks.length;
      const decisionLabels = getDecisionLabels(g);

      // Additive statistics for total metrics
      const tHits = Number(g.total_hits || 0);
      const pHits = Number(g.phrase_hits || 0);
      const finalScore = g.score || (tHits + pHits + extCount);
      const finalLevel = g.risk_level || hitsRiskLevel(finalScore);
      
      const link = g.group_link || g.link || '';
      const normalizedLink = link.toLowerCase().replace(/\/$/, '').trim();
      const groupKey = normalizedLink ? `link:${normalizedLink}` : (g.username || 'unknown');

      return {
        groupKey,
        groupName:       g.group_title || g.username || 'Unknown',
        groupLink:       link,
        totalHits:       tHits,
        phraseHits:      pHits,
        externalLinks:   extCount,
        externalLinksTotal: extCount,
        // Evidence samples intentionally not saved in Firestore to keep scan_reports under 1 MiB.
        // Uncomment later if we move evidence to per-group docs/subcollections.
        // externalLinkItems: compactListForDb(rawExternalLinks, 30, 220),
        memberCount:     g.member_count ?? (g.group_info?.member_count) ?? null,
        riskLevel:       finalLevel,
        score:           finalScore,
        reviewStatus:    'unreviewed',
        scanMode:        'unified',
        isPublic:        g.is_public ?? (g.isPublic ?? (g.group_info?.is_public ?? null)),
        hasPhoto:        g.has_photo ?? null,
        matchedKeywords: compactMatchedKeywordsForDb(rawMatchedKeywords),
        // matchedKeywordEvidence: compactMatchedKeywordEvidenceForDb(rawMatchedKeywords, 30),
        description:     truncateForDb(g.group_description || '', 500),
        adminCount:      g.admin_count ?? null,
        isVerified:      g.is_verified ?? false,
        lastMatched:     g.last_matched || null,
        llmLabel:        g.llm_label || g.llmLabel || null,
        llmDecision:     decisionLabels.aiLabel,
        llmReason:       truncateForDb(g.llm_reason || g.llmReason || '', 2000),
        llmConfidence:   g.llm_confidence ?? g.llmConfidence ?? null,
        keywordRelated:  g.keyword_related ?? g.keywordRelated ?? null,
        keywordRelatedReason: truncateForDb(g.keyword_related_reason || g.keywordRelatedReason || '', 500),
        aiLabel:         decisionLabels.aiLabel,
        matchedKeywordLabel: decisionLabels.matchedKeywordLabel,
        externalLinkLabel: decisionLabels.externalLinkLabel,
        overallLabel:    decisionLabels.overallLabel,
        decisionLabelOrder: ['aiLabel', 'matchedKeywordLabel', 'externalLinkLabel', 'overallLabel'],
        phraseMatchesTotal: rawPhraseMatches.length,
        // phraseMatches: compactListForDb(rawPhraseMatches, 30, 700),
        discoveredByEngines: g.discovered_by_engines || g.discoveredByEngines || [],
        locationRelevant: g.location_relevant ?? g.locationRelevant ?? (g.goa_relevant ?? g.goaRelevant ?? false),
        locationMatchesTotal: rawLocationMatches.length,
        locationTarget: g.location_target ?? g.locationTarget ?? {
          state: selectedState,
          city: selectedCity,
          label: [selectedCity, selectedState].filter(Boolean).join(', '),
          queryMode: queryLocationMode,
        },
        goaRelevant:     g.goa_relevant ?? g.goaRelevant ?? false,
        goaMatchesTotal: rawGoaMatches.length,
        // goaMatches: compactListForDb(rawGoaMatches, 30, 500),
        storageMode:     'compact',
      };
    });

    const matchedKeywordsFound = [...new Set(
      groupsDetails.flatMap(g => Object.keys(g.matchedKeywords || {}))
    )];
    const totalPhraseHits = groupsDetails.reduce((sum, g) => sum + (g.phraseHits || 0), 0);
    const totalKeywordHits = groupsDetails.reduce((sum, g) => sum + (g.totalHits || 0), 0);
    const scanGroupKeys = groupsDetails.map(g => g.groupKey).filter(Boolean);
    const publicCount = groupsDetails.filter(g => g.isPublic === true).length;
    const privateCount = groupsDetails.filter(g => g.isPublic === false).length;
    const locationTarget = data.location_target || {
      state: selectedState,
      city: selectedCity,
      label: [selectedCity, selectedState].filter(Boolean).join(', '),
      queryMode: queryLocationMode,
    };
    const searchQueriesUsed = Array.isArray(data.search_queries_used) && data.search_queries_used.length > 0
      ? data.search_queries_used
      : buildSearchQueriesUsed(data.translations || {}, locationTarget);

    const payload = {
      id: docId, scanType: 'unified',
      keywords: data.phrases_searched || [],
      keywordQuery: data.keyword_phrase || '',
      searchQueriesUsed,
      searchQueriesUsedCount: searchQueriesUsed.length,
      scanLocationState: String(locationTarget.state || '').trim(),
      scanLocationCity: String(locationTarget.city || '').trim(),
      scanLocationLabel: String(locationTarget.label || buildLocationLabel(locationTarget)).trim(),
      locationTarget,
      matchedKeywordsFound,
      groupsDiscovered: groupsDetails.length,
      totalHits: totalPhraseHits + totalKeywordHits,
      totalPhraseHits,
      totalKeywordHits,
      publicCount,
      privateCount,
      phraseMatchCount: groupsDetails.filter(g => g.phraseHits > 0).length,
      discoveryCount: groupsDetails.filter(g => !(g.phraseHits > 0)).length,
      groupsDetails, scanFingerprint: fp,
      scanGroupKeys,
      operationId: rawOpId || '',
      officerId: String(user?.email || 'admin').trim().toLowerCase(),
      scannerEmail: String(user?.email || '').trim().toLowerCase(),
      scannerUid: String(user?.uid || '').trim(),
      source: 'unified-search',
      storageMode: 'compact',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    try {
      if (apiStatus === 'available') {
        const res = await scanReportsService.createOrUpdate(payload);
        const savedId = res.reportId || res.id || docId;
        setSavedReportId(savedId);
        savedKeyRef.current = fp;
        try {
          await setDoc(doc(db, 'scan_reports', savedId), {
            ...payload, id: savedId,
            updatedAt: serverTimestamp(), updatedAtMs: Date.now(),
            createdAt: serverTimestamp(), createdAtMs: Date.now(),
          }, { merge: true });
        } catch { /* firebase fallback silent */ }
      } else {
        await setDoc(doc(db, 'scan_reports', docId), {
          ...payload,
          updatedAt: serverTimestamp(), updatedAtMs: Date.now(),
          createdAt: serverTimestamp(), createdAtMs: Date.now(),
        }, { merge: true });
        setSavedReportId(docId);
        savedKeyRef.current = fp;
      }
    } catch (err) {
      console.error('[UnifiedSave]', err);
    } finally {
      savingRef.current = false;
    }
  }, [user, apiStatus, selectedState, selectedCity, queryLocationMode]);

  /* ── load last results (display only, no save) ── */
  const loadResultsOnly = useCallback(async () => {
    try {
      const headers = await getFirebaseAuthHeaders();
      const r = await fetch(`${BACKEND_ROOT}/telegram/unified-search-results`, { headers });
      if (r.ok) { const d = await r.json(); if (d?.data) setResults(d.data); }
    } catch { /* silent */ }
  }, []);

  /* ── load AND save (called after scan completes) ── */
  const loadAndSave = useCallback(async (opId) => {
    try {
      const headers = await getFirebaseAuthHeaders();
      const qs = opId ? `?operation_id=${encodeURIComponent(opId)}` : '';
      const r = await fetch(`${BACKEND_ROOT}/telegram/unified-search-results${qs}`, { headers });
      if (r.ok) {
        const d = await r.json();
        if (d?.data) { setResults(d.data); await saveToDb(d.data, opId); }
      }
    } catch { /* silent */ }
  }, [saveToDb]);

  /* ── load history ── */
  const fetchHistoricalScans = useCallback(async (page = 0, force = false) => {
    if (!force && historyItems[page]) {
      setCurrentHistoryPage(page);
      return;
    }
    setHistoryLoading(true);
    try {
      if (apiStatus === 'available') {
        const normalizedEmail = String(user?.email || '').trim().toLowerCase();
        const normalizedUid = String(user?.uid || '').trim();
        if (!normalizedUid && !normalizedEmail) {
          setHistoryItems([]);
          setHistoryHasMoreByPage({});
          return;
        }
        const response = await scanReportsService.getReportsByOfficer(
          normalizedEmail,
          normalizedUid,
          'unified',
          1,
          page
        );
        const reports = response.reports || response.data || (Array.isArray(response) ? response : []);
        const currentReport = reports
          .find(t => t.id?.startsWith('unified_') || t.scanType === 'unified') || null;

        setHistoryItems(prev => {
          const next = [...prev];
          next[page] = currentReport;
          return next;
        });
        setHistoryHasMoreByPage(prev => ({ ...prev, [page]: Boolean(response.hasMore) }));
        setCurrentHistoryPage(page);
      } else {
        setHistoryItems([]);
        setHistoryHasMoreByPage({});
      }
    } catch (e) {
      console.error('History fetch error', e);
    } finally {
      setHistoryLoading(false);
    }
  }, [apiStatus, historyItems, user?.email, user?.uid]);

  /* mount: display last results, no DB save */
  useEffect(() => { loadResultsOnly(); }, [loadResultsOnly]);

  /* resume from sessionStorage */
  useEffect(() => {
    const stored = sessionStorage.getItem(activeUnifiedScanStorageKey);
    if (!stored) return;
    try {
      const { opId, kw } = JSON.parse(stored);
      if (opId && kw) {
        setOperationId(opId); setKeyword(kw); setScanning(true);
        setScanProgress({ status: 'running', current_phase: 'resuming', progress: 0, percentage: 0 });
      }
    } catch { sessionStorage.removeItem(activeUnifiedScanStorageKey); }
  }, [activeUnifiedScanStorageKey]);

  /* progress polling */
  useEffect(() => {
    if (!scanning || !operationId) return;
    const iv = setInterval(async () => {
      try {
        const r = await fetch(`${BACKEND_ROOT}/progress/${operationId}`);
        if (!r.ok) return;
        const data = await r.json();
        const prog = data?.progress || data;
        if (prog?.user_message) {
          setScanNotice({
            text: formatUiError(prog.user_message),
            level: prog.message_level || 'info',
          });
        }
        setScanProgress(prev => {
          const incomPct  = deriveProgressPercent(prog);
          const done      = prog?.status === 'completed';
          const nextPct = done ? 100 : Math.max(incomPct, 2);
          return { ...(prev || {}), ...prog,
            progress:   nextPct,
            percentage: nextPct };
        });
        if (prog?.status === 'completed' || prog?.status === 'failed') {
          clearInterval(iv);
          sessionStorage.removeItem(activeUnifiedScanStorageKey);
          setScanning(false);
          if (prog?.status === 'failed') {
            setError(formatUiError(prog?.user_message || 'The current scan failed before completion.'));
          }
          if (prog?.status === 'completed') setTimeout(() => loadAndSave(operationId), 1500);
        }
      } catch { /* ignore */ }
    }, 5000);
    return () => clearInterval(iv);
  }, [scanning, operationId, loadAndSave, activeUnifiedScanStorageKey]);

  /* ── AI Phrase Generation ── */
  const generatePhrases = async () => {
    if (!aiContext) return;
    setIsGenerating(true); setError(null);
    try {
      const res = await fetch(`${BACKEND_ROOT}/phrases/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ context: aiContext })
      });
      const data = await res.json();
      const cleaned = Array.isArray(data?.phrases)
        ? data.phrases.map((p) => sanitizePhraseToken(p)).filter(Boolean)
        : [];
      const uniqueCleaned = [...new Set(cleaned)];
      setGeneratedPhraseBatches((prev) => {
        const existing = new Set(prev.flatMap((batch) => batch.phrases || []));
        const newPhrases = uniqueCleaned.filter((phrase) => !existing.has(phrase));
        if (newPhrases.length === 0) return prev;
        const nextBatchId = (prev[prev.length - 1]?.id || 0) + 1;
        return [
          ...prev.map((batch) => ({ ...batch, isLatest: false })),
          {
            id: nextBatchId,
            context: aiContext.trim(),
            phrases: newPhrases,
            type: 'related',
            isLatest: true,
          },
        ];
      });
    } catch (e) {
      setError("Failed to generate AI phrases.");
    } finally {
      setIsGenerating(false);
    }
  };

  const generateHashedPhrases = async () => {
    if (!aiContext) return;
    setIsGeneratingHashed(true); setError(null);
    try {
      const res = await fetch(`${BACKEND_ROOT}/phrases/generate-hashed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ context: aiContext, count: 20 })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || 'Failed to generate hashed words.');
      }
      const cleaned = Array.isArray(data?.phrases)
        ? data.phrases.map((p) => sanitizePhraseToken(p)).filter(Boolean)
        : [];
      const uniqueCleaned = [...new Set(cleaned)];
      setGeneratedPhraseBatches((prev) => {
        const existing = new Set(prev.flatMap((batch) => batch.phrases || []));
        const newPhrases = uniqueCleaned.filter((phrase) => !existing.has(phrase));
        if (newPhrases.length === 0) return prev;
        const nextBatchId = (prev[prev.length - 1]?.id || 0) + 1;
        return [
          ...prev.map((batch) => ({ ...batch, isLatest: false })),
          {
            id: nextBatchId,
            context: aiContext.trim(),
            phrases: newPhrases,
            type: 'hashed',
            isLatest: true,
          },
        ];
      });
    } catch (e) {
      setError(e?.message || "Failed to generate hashed words.");
    } finally {
      setIsGeneratingHashed(false);
    }
  };

  const togglePhrase = (p) => {
    setSelectedPhrases(prev => {
      const next = prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p];
      setKeyword(next.join(' OR '));
      return next;
    });
  };

  /* start scan ── */
  const startSearch = async () => {
    if (!keyword.trim()) return;
    const effectiveLocationMode = isAnyStateSearch ? 'any' : queryLocationMode;
    const effectiveState = isAnyStateSearch ? '' : selectedState;
    const effectiveCity = isAnyStateSearch ? '' : selectedCity;

    if (!isAnyStateSearch && !selectedState.trim()) {
      setError('Please select a state before starting search.');
      return;
    }
    if (effectiveLocationMode === 'both' && !effectiveCity.trim()) {
      setError('Please enter a city when using the state + city query mode.');
      return;
    }

    if (effectiveLocationMode === 'city_only' && !effectiveCity.trim()) {
      setError('Please enter a city when using the city-only query mode.');
      return;
    }
    setError(null); setScanNotice(null); setResults(null); setScanning(true);
    setOperationId(null); setSavedReportId(''); savedKeyRef.current = null;
    setScanProgress({ status: 'running', current_phase: 'initializing', progress: 2, percentage: 2 });
    try {
      const headers = await getFirebaseAuthHeaders({ 'Content-Type': 'application/json' });
      const res = await fetch(`${BACKEND_ROOT}/telegram/unified-search`, {
        method: 'POST', headers,
        body: JSON.stringify({
          keyword_phrase: keyword.trim(),
          max_groups: maxGroups,
          message_limit: msgLimit,
          search_languages: selectedLanguages,
          target_state: effectiveState,
          target_city: effectiveCity,
          query_location_mode: effectiveLocationMode,
        }),
      });
      if (res.status === 409) {
        const d = await res.json();
        setError(formatUiError('Scan already running - reconnecting to the active scan...'));
        setOperationId(d.operation_id);
        sessionStorage.setItem(activeUnifiedScanStorageKey, JSON.stringify({ opId: d.operation_id, kw: keyword.trim() }));
        return;
      }
      if (!res.ok) throw new Error(`${res.status} – ${await res.text()}`);
      const d = await res.json();
      setOperationId(d.operation_id);
      sessionStorage.setItem(activeUnifiedScanStorageKey, JSON.stringify({ opId: d.operation_id, kw: keyword.trim() }));
    } catch (e) {
      setError(formatUiError('Failed to start: ' + e.message));
      setScanning(false); setScanProgress(null);
    }
  };

  /* ── Reviews Handling ── */
  const updateLastScanReportReviewCounts = useCallback(async (group, status, reviewMeta = {}) => {
    const reportId = group?.scanReportId || group?.scan_report_id || savedReportId;
    if (!reportId) return;

    const groupKey = getReviewGroupKey(group);
    if (!groupKey) return;

    if (apiStatus === 'available') {
      try {
        await scanReportsService.updateCounts(reportId, {
          groupKey,
          newStatus: status,
          source: 'telegram-search-review'
        });
      } catch (error) {
        console.warn('Backend API update failed, falling back to Firebase:', error);
      }
    }

    if (apiStatus !== 'available') {
      const reportRef = doc(db, 'scan_reports', reportId);
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(reportRef);
        if (!snap.exists()) return;

        const data = snap.data() || {};
        const existingStatuses = data.groupStatuses || {};
        const nextStatuses = { ...existingStatuses, [groupKey]: status };

        const storedKeys = Array.isArray(data.scanGroupKeys) ? data.scanGroupKeys.filter(Boolean) : [];
        const currentScanKeys = [
          ...(results?.phrase_match_groups || []),
          ...(results?.discovery_groups || [])
        ].map(getReviewGroupKey).filter(Boolean);
        const scanKeys = [...new Set((storedKeys.length ? storedKeys : currentScanKeys))];

        let fraudulentCount = 0;
        let genuineCount = 0;
        let suspiciousCount = 0;
        scanKeys.forEach((key) => {
          if (nextStatuses[key] === 'fake') fraudulentCount += 1;
          if (nextStatuses[key] === 'genuine') genuineCount += 1;
          if (nextStatuses[key] === 'suspicious') suspiciousCount += 1;
        });

        const groupsDiscovered = scanKeys.length;
        const unmarkedCount = Math.max(groupsDiscovered - fraudulentCount - genuineCount - suspiciousCount, 0);

        const groupsDetails = Array.isArray(data.groupsDetails) ? data.groupsDetails : [];
        const nextGroupsDetails = groupsDetails.map((item) => {
          if (getReviewGroupKey(item) !== groupKey) return item;
          return {
            ...item,
            reviewStatus: status,
            reviewedAt: reviewMeta.timestamp || new Date().toISOString(),
            reviewedAtMs: reviewMeta.timestampMs || Date.now(),
            reviewedBy: reviewMeta.officerId || '',
            reviewReason: reviewMeta.reason || '',
            blockedStatus: reviewMeta.blockedStatus ?? (status === 'fake' ? 'not_blocked' : null),
          };
        });

        tx.update(reportRef, {
          groupStatuses: nextStatuses,
          scanGroupKeys: scanKeys,
          groupsDiscovered,
          fraudulentCount,
          genuineCount,
          suspiciousCount,
          unmarkedCount,
          groupsDetails: nextGroupsDetails,
          updatedAt: serverTimestamp()
        });
      });
    }
  }, [savedReportId, results, apiStatus]);

  const handleMarkReview = async (group, status) => {
    const reviewLabel = status === 'fake' ? 'fraudulent' : status === 'suspicious' ? 'suspicious' : 'genuine';
    const reason = window.prompt(`Please enter reason why this group is ${reviewLabel}:`, '');

    if (reason === null) return;
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setError('Reason is required before submitting review.');
      return;
    }

    const reviewerId = user?.email || 'admin';
    const blockedStatus = status === 'fake' ? 'not_blocked' : null;
    const proceed = window.confirm(`Confirm marking this group as ${reviewLabel}?\n\nReason: ${trimmedReason}`);
    if (!proceed) return;

    try {
      const { groupLink, groupName } = getReviewGroupIdentity(group);
      const reportId = group?.scanReportId || group?.scan_report_id || historyItems[currentHistoryPage]?.id || savedReportId;
      const keywordQuery =
        group.keywordQuery ||
        group.searchKeyword ||
        results?.keyword_phrase ||
        '';

      const reviewData = {
        groupLink,
        groupName,
        status,
        reason: trimmedReason,
        timestamp: new Date().toISOString(),
        officerId: reviewerId,
        source: 'telegram_search_review',
        keywordQuery,
        sEngine: group.discoveredByEngines || group.discovered_by_engines || [],
        blockedStatus,
        scanReportIds: reportId ? [reportId] : [],
      };

      if (apiStatus === 'available') {
        try {
          await groupReviewsService.createReview(reviewData);
        } catch (apiError) {
          console.warn('Backend API review save failed, using Firebase fallback:', apiError);
          await addDoc(collection(db, 'group_reviews'), { ...reviewData, timestamp: serverTimestamp() });
        }
      } else {
        await addDoc(collection(db, 'group_reviews'), { ...reviewData, timestamp: serverTimestamp() });
      }

      setGroupReviews(prev => {
        const next = { ...prev };
        upsertLatestReview(next, { ...reviewData, timestampMs: Date.now() });
        return next;
      });

      const reviewMeta = { ...reviewData, timestampMs: Date.now() };

      setHistoryItems((prev) => prev.map((report) => {
        if (!report || reportId !== report.id) {
          return report;
        }
        const details = Array.isArray(report.groupsDetails) ? report.groupsDetails : [];
        return {
          ...report,
          groupsDetails: details.map((item) => (
            getReviewGroupKey(item) === getReviewGroupKey(group)
              ? {
                  ...item,
                  reviewStatus: status,
                  reviewedAt: reviewMeta.timestamp,
                  reviewedAtMs: reviewMeta.timestampMs,
                  reviewedBy: reviewMeta.officerId,
                  reviewReason: reviewMeta.reason,
                  blockedStatus: reviewMeta.blockedStatus,
                }
              : item
          )),
        };
      }));

      await updateLastScanReportReviewCounts(group, status, reviewMeta);
    } catch (err) {
      setError(`Error saving review: ${err.message}`);
    }
  };

  const handleBlockedStatusChange = async (group, blockedStatus) => {
    const reviewerId = user?.email || 'admin';
    const { groupLink, groupName } = getReviewGroupIdentity(group);
    const reportId = group?.scanReportId || group?.scan_report_id || historyItems[currentHistoryPage]?.id || savedReportId;
    const payload = {
      groupLink,
      groupName,
      blockedStatus,
      officerId: reviewerId,
      scanReportIds: reportId ? [reportId] : [],
    };

    try {
      if (apiStatus === 'available') {
        await groupReviewsService.updateBlockedStatus(payload);
      } else {
        await addDoc(collection(db, 'group_reviews'), {
          ...payload,
          status: 'fake',
          reason: 'Blocked status updated from Telegram search',
          source: 'telegram_search_blocked_status',
          timestamp: serverTimestamp(),
          timestampMs: Date.now(),
        });
      }

      const reviewDoc = {
        ...payload,
        blockedStatus,
        blockedStatusUpdatedBy: reviewerId,
        blockedStatusUpdatedAtMs: Date.now(),
      };

      setGroupReviews((prev) => {
        const next = { ...prev };
        const existing = prev[getReviewGroupKey(group)] || {};
        upsertLatestReview(next, { ...existing, ...reviewDoc, timestampMs: Date.now() });
        return next;
      });

      setHistoryItems((prev) => prev.map((report) => {
        if (!report || report.id !== reportId) return report;
        const details = Array.isArray(report.groupsDetails) ? report.groupsDetails : [];
        return {
          ...report,
          groupsDetails: details.map((item) => (
            getReviewGroupKey(item) === getReviewGroupKey(group)
              ? {
                  ...item,
                  blockedStatus,
                  reviewedAtMs: Date.now(),
                }
              : item
          )),
        };
      }));
    } catch (err) {
      setError(`Error updating blocked status: ${err.message}`);
    }
  };

  /* ── derived data ── */
  const discoveryGroups = useMemo(() => results?.discovery_groups    || [], [results]);
  const activeResultsAreAnyState = (results?.location_target?.queryMode || results?.locationTarget?.queryMode) === 'any';

  /**
   * Combined list:
   * 1. FILTER expired / inaccessible discovery groups via isValidDiscoveryGroup
   * 2. KEEP reviewed/marked groups visible so the UI can show their status tags
   * 3. SORT by AI label priority (fraud → uncertain → genuine), then hits desc
   */
  const combinedSorted = useMemo(() => {
    const all = discoveryGroups
      .filter(isValidDiscoveryGroup)
      .map(g => {
        const labels = getDecisionLabels(g);
        return {
          _type: g.phrase_hits > 0 ? 'phrase' : 'discovery',
          _risk: g.risk_level || hitsRiskLevel(g.score),
          _hits: Number(g.score || 0),
          _members: Number(g.memberCount ?? g.member_count ?? g.group_info?.member_count ?? 0),
          _ai: labels.aiLabel,
          _overall: labels.overallLabel,
          _keywordRelatedRank: keywordRelatedRank(g),
          _signals: labels.signals,
          _location: g.location_relevant || g.locationRelevant || g.goa_relevant || g.goaRelevant || false,
          // Extract last active date for sorting
          _lastActiveStr: g.lastActiveDate ?? g.last_active_date ?? g.lastMessageDate ?? g.last_message_date ?? g.group_info?.last_active_date ?? null,
          _data: g,
        };
      });

    all.sort((a, b) => {
      // 1. First surface groups that the LLM says match the search topic.
      const keywordRelatedDelta = b._keywordRelatedRank - a._keywordRelatedRank;
      if (keywordRelatedDelta !== 0) return keywordRelatedDelta;

      // 2. Then by overall decision (fraud, uncertain, genuine)
      const overallDelta = (AI_SORT_RANK[b._overall] || 0) - (AI_SORT_RANK[a._overall] || 0);
      if (overallDelta !== 0) return overallDelta;

      // 3. Optional secondary sort within the same AI bucket
      const aTime = a._lastActiveStr ? new Date(a._lastActiveStr).getTime() : 0;
      const bTime = b._lastActiveStr ? new Date(b._lastActiveStr).getTime() : 0;
      if (secondarySort === 'member_count' && b._members !== a._members) {
        return b._members - a._members;
      }
      if (secondarySort === 'recent_active' && bTime !== aTime && !isNaN(bTime) && !isNaN(aTime)) {
        return bTime - aTime;
      }

      // 4. PRIORITY: selected location groups
      if (a._location !== b._location) return a._location ? -1 : 1;

      // 5. Then by Hits/Score
      const scoreDelta = b._hits - a._hits;
      if (scoreDelta !== 0) return scoreDelta;

      return (RISK_RANK[b._risk] || 0) - (RISK_RANK[a._risk] || 0);
    });
    return all;
  }, [discoveryGroups, secondarySort]);

  const filteredItems = useMemo(() =>
    combinedSorted.filter(item => {
      // 1. Location Filter
      const passLocation = activeResultsAreAnyState || locationFilter === 'all' || item._location;
      
      // 2. Overall decision filter
      let passOverall = true;
      if (overallFilter === 'Overall-Fraud')     passOverall = (item._overall === 'fraud');
      if (overallFilter === 'Overall-Genuine')   passOverall = (item._overall === 'genuine');
      if (overallFilter === 'Overall-Uncertain') passOverall = (item._overall === 'uncertain');
      
      return passLocation && passOverall;
    }),
    [combinedSorted, activeResultsAreAnyState, locationFilter, overallFilter]
  );

  const historySortedGroups = useMemo(() => {
    const currentScan = historyItems[currentHistoryPage];
    if (!currentScan) return [];
    const historyIsAnyState = (currentScan.locationTarget?.queryMode || currentScan.location_target?.queryMode) === 'any';

    const all = (currentScan.groupsDetails || [])
      .filter(isValidDiscoveryGroup)
      .map((g) => {
        const labels = getDecisionLabels(g);
        return {
          _type: (g.phraseHits ?? g.phrase_hits ?? 0) > 0 ? 'phrase' : 'discovery',
          _risk: g.riskLevel ?? g.risk_level ?? hitsRiskLevel(g.score ?? 0),
          _hits: Number(g.score ?? g.totalHits ?? g.total_hits ?? 0),
          _members: Number(g.memberCount ?? g.member_count ?? g.group_info?.member_count ?? 0),
          _ai: labels.aiLabel,
          _overall: labels.overallLabel,
          _keywordRelatedRank: keywordRelatedRank(g),
          _signals: labels.signals,
          _location: g.location_relevant || g.locationRelevant || g.goa_relevant || g.goaRelevant || false,
          _lastActiveStr: g.lastActiveDate ?? g.last_active_date ?? g.lastMessageDate ?? g.last_message_date ?? g.group_info?.last_active_date ?? null,
          _data: { ...g, scanReportId: currentScan.id },
        };
      });

    all.sort((a, b) => {
      const keywordRelatedDelta = b._keywordRelatedRank - a._keywordRelatedRank;
      if (keywordRelatedDelta !== 0) return keywordRelatedDelta;

      const overallDelta = (AI_SORT_RANK[b._overall] || 0) - (AI_SORT_RANK[a._overall] || 0);
      if (overallDelta !== 0) return overallDelta;

      const aTime = a._lastActiveStr ? new Date(a._lastActiveStr).getTime() : 0;
      const bTime = b._lastActiveStr ? new Date(b._lastActiveStr).getTime() : 0;
      if (secondarySort === 'member_count' && b._members !== a._members) return b._members - a._members;
      if (secondarySort === 'recent_active' && bTime !== aTime && !isNaN(bTime) && !isNaN(aTime)) return bTime - aTime;

      if (a._location !== b._location) return a._location ? -1 : 1;

      const scoreDelta = b._hits - a._hits;
      if (scoreDelta !== 0) return scoreDelta;

      return (RISK_RANK[b._risk] || 0) - (RISK_RANK[a._risk] || 0);
    });

    return all.filter((item) => {
      const passLocation = historyIsAnyState || locationFilter === 'all' || item._location;

      let passOverall = true;
      if (overallFilter === 'Overall-Fraud') passOverall = item._overall === 'fraud';
      if (overallFilter === 'Overall-Genuine') passOverall = item._overall === 'genuine';
      if (overallFilter === 'Overall-Uncertain') passOverall = item._overall === 'uncertain';

      return passLocation && passOverall;
    });
  }, [historyItems, currentHistoryPage, secondarySort, locationFilter, overallFilter]);

  const csvExportItems = showHistory ? historySortedGroups : filteredItems;
  const canDownloadCsv = csvExportItems.length > 0;
  const handleDownloadCsv = () => {
    const groups = csvExportItems.map((item) => item._data || item);
    const source = showHistory ? historyItems[currentHistoryPage] : results;
    const sourceId = showHistory
      ? (source?.id || `history-page-${currentHistoryPage + 1}`)
      : (results?.operation_id || results?.operationId || savedReportId || 'current-scan');
    const safeId = String(sourceId).replace(/[^a-z0-9_-]+/gi, '-').replace(/^-+|-+$/g, '') || 'telegram-scan';
    const csv = buildTelegramGroupsCsv(groups, groupReviews);
    downloadCsvFile(`telegram-search-${safeId}.csv`, csv);
  };

  const stats = {
    total:     combinedSorted.length,
    phrase:    combinedSorted.filter(i => i._data.phrase_hits > 0).length,
    discovery: combinedSorted.filter(i => !(i._data.phrase_hits > 0)).length,
    critical:  combinedSorted.filter(i => i._risk === 'critical').length,
    high:      combinedSorted.filter(i => i._risk === 'high').length,
    medium:    combinedSorted.filter(i => i._risk === 'medium').length,
    low:       combinedSorted.filter(i => i._risk === 'low').length,
    aiGenuine: combinedSorted.filter(i => i._ai === 'genuine').length,
    aiFraud: combinedSorted.filter(i => i._ai === 'fraud').length,
    aiUncertain: combinedSorted.filter(i => i._ai === 'uncertain').length,
    overallFraud: combinedSorted.filter(i => i._overall === 'fraud').length,
    overallUncertain: combinedSorted.filter(i => i._overall === 'uncertain').length,
    overallGenuine: combinedSorted.filter(i => i._overall === 'genuine').length,
    externalFraud: combinedSorted.filter(i => i._signals.externalStatus === 'fraud').length,
    externalGenuine: combinedSorted.filter(i => i._signals.externalStatus === 'genuine').length,
    keywordFraud: combinedSorted.filter(i => i._signals.keywordStatus === 'fraud').length,
    keywordGenuine: combinedSorted.filter(i => i._signals.keywordStatus === 'genuine').length,
    locationCount: combinedSorted.filter(i => i._location).length,
    nonLocationCount: combinedSorted.filter(i => !i._location).length,
  };

  /* ══════════════════════════════════════════════════════════════ */
  return (
    <div className="page-shell phrase-page">

      {/* ── Header ── */}
      <header className="top-bar">
        <div>
          <h1 className="page-title">Telegram One Search</h1>
          <p className="page-subtitle">
            Translate · Discover · Phrase - All in one scan.
          </p>
        </div>
      </header>

      <div className="dash-grid phrase-dash-grid">
        {/* ── AI Phrase Gen Card ── */}
        <div className="card input-card">
          <div className="section-head">
            <div>
              <div className="eyebrow">AI TOOLS</div>
              <h3 className="section-title">Generate Related Keyword</h3>
            </div>
          </div>
          <div className="phrase-input-row" style={{ marginBottom: 16 }}>
            <input
              className="form-input"
              placeholder="e.g. loan scam, drug delivery, fake passport..."
              value={aiContext}
              onChange={e => setAiContext(e.target.value)}
            />
            <button className="primary-btn phrase-compact-btn" onClick={generatePhrases} disabled={isGenerating}>
              {isGenerating ? 'Generating...' : 'Generate'}
            </button>
            <button
              className="primary-btn phrase-compact-btn"
              type="button"
              onClick={generateHashedPhrases}
              disabled={isGeneratingHashed}
              title="Generate fuzzy/obfuscated search variants such as n*des or dr*g delivery"
            >
              {isGeneratingHashed ? 'Hashing...' : 'Hash'}
            </button>
          </div>

          {generatedPhrases.length > 0 && (
            <div className="phrase-block-gap" style={{ marginTop: 16 }}>
              <p className="eyebrow phrase-inline-label" style={{ marginBottom: 8 }}>Select logic to inject</p>
              {latestGeneratedBatch && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                    <span className="eyebrow phrase-inline-label" style={{ marginBottom: 0, fontSize: '0.72rem' }}>
                      {latestGeneratedBatch.type === 'hashed' ? 'Latest Hashed Suggestions' : 'Latest Suggestions'}
                    </span>
                    {latestGeneratedBatch.context && (
                      <span className="chip" style={{ fontSize: '0.72rem', padding: '3px 8px', background: '#f8fafc', color: '#475569' }}>
                        {latestGeneratedBatch.context}
                      </span>
                    )}
                  </div>
                  <div className="chips">
                    {latestGeneratedBatch.phrases.map((p) => {
                      const sel = selectedPhrases.includes(p);
                      return (
                        <button key={`${latestGeneratedBatch.id}-${p}`} className="chip"
                          onClick={() => togglePhrase(p)}
                          style={{
                            background: sel ? '#174a2c' : '#eefbf3',
                            color: sel ? '#fff' : '#166534',
                            border: sel ? 'none' : '1px solid #bbf7d0',
                            cursor: 'pointer',
                            fontWeight: sel ? 700 : 500
                          }}
                        >
                          {p}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* keyword */}
          <label style={{ display: 'block', fontWeight: 600, fontSize: 14, color: '#0f172a', marginBottom: 6 }}>
            Keyword / Phrase
            <span className="muted" style={{ fontWeight: 400, marginLeft: 8, fontSize: 13 }}>
              Separate with commas or OR
            </span>
          </label>
          <input
            id="unified-keyword-input"
            className="form-input"
            value={keyword}
            onChange={e => setKeyword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !scanning && startSearch()}
            placeholder='"drug delivery" OR "fake passport"'
            disabled={scanning}
            style={{ marginBottom: 14 }}
          />

          <div style={{ marginBottom: 4 }}>
            <label style={{ display: 'block', fontWeight: 600, fontSize: 14, color: '#0f172a', marginBottom: 8 }}>
              Search languages
            </label>
            <div className="chips" style={{ gap: 8 }}>
              {LANGUAGE_OPTIONS.map((option) => {
                const checked = selectedLanguages.includes(option.key);
                return (
                  <button
                    key={option.key}
                    type="button"
                    className="chip"
                    onClick={() => {
                      setSelectedLanguages((prev) => {
                        const next = checked
                          ? prev.filter((lang) => lang !== option.key)
                          : [...prev, option.key];
                        return next.includes('english') ? next : ['english', ...next];
                      });
                    }}
                    style={{
                      background: checked ? '#174a2c' : '#f1f5f9',
                      color: checked ? '#fff' : '#475569',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── Search Config Card ── */}
        <div className="card input-card">
          <div className="section-head">
            <div>
              <div className="eyebrow">TARGET CONFIGURATION</div>
              <h3 className="section-title">ONE SEARCH</h3>
            </div>
          </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 14 }}>
          <div>
            <label style={{ display: 'block', fontWeight: 600, fontSize: 14, color: '#0f172a', marginBottom: 6 }}>
              State
              <span className="muted" style={{ fontWeight: 400, marginLeft: 8, fontSize: 13 }}>
                {isAnyStateSearch ? 'General search' : 'Required'}
              </span>
            </label>
            <select
              className="form-input"
              value={selectedState}
              onChange={(e) => {
                setSelectedState(e.target.value);
                if (e.target.value === ANY_STATE_VALUE) {
                  setSelectedCity('');
                  setQueryLocationMode('any');
                  setLocationFilter('all');
                } else if (queryLocationMode === 'any') {
                  setQueryLocationMode('state_only');
                  setLocationFilter('focused');
                }
              }}
              disabled={scanning}
            >
              <option value={ANY_STATE_VALUE}>Any State</option>
              {INDIA_STATE_OPTIONS.map((stateOption) => (
                <option key={stateOption} value={stateOption}>
                  {stateOption}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 600, fontSize: 14, color: '#0f172a', marginBottom: 6 }}>
              City
            </label>
            <input
              className="form-input"
              value={selectedCity}
              onChange={(e) => setSelectedCity(e.target.value)}
              placeholder={isAnyStateSearch ? 'Disabled for Any State' : 'Optional city'}
              disabled={scanning || isAnyStateSearch}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 600, fontSize: 14, color: '#0f172a', marginBottom: 6 }}>
                Select location to be used
            </label>
            <select
              className="form-input"
              value={isAnyStateSearch ? 'any' : queryLocationMode}
              onChange={(e) => setQueryLocationMode(e.target.value)}
              disabled={scanning || isAnyStateSearch}
            >
              
              <option value="any">Use keyword only</option>
              <option value="state_only">Use State only</option>
              <option value="city_only">Use City only</option>
              <option value="both">Use City + State</option>
            </select>
          </div>
        </div>


        {error && <div className="error-banner phrase-block-small-gap" style={{ marginBottom: 16 }}>{error}</div>}
        {!error && scanNotice?.text && (
          <div
            className="phrase-block-small-gap"
            style={{
              marginBottom: 16,
              padding: '11px 14px',
              borderRadius: 10,
              fontSize: 14,
              fontWeight: 600,
              border: `1px solid ${scanNotice.level === 'warning' ? '#fde68a' : '#bfdbfe'}`,
              background: scanNotice.level === 'warning' ? '#fffbeb' : '#eff6ff',
              color: scanNotice.level === 'warning' ? '#92400e' : '#1d4ed8'
            }}
          >
            {scanNotice.text}
          </div>
        )}

        <button id="unified-search-start-btn" className="primary-btn"
          style={{ width: 'auto' }}
          onClick={startSearch}
          disabled={scanning || !keyword.trim() || (!isAnyStateSearch && !selectedState.trim()) || apiOnline === false}>
          {scanning ? 'Scanning…' : 'Start Search'}
        </button>

        {/* ── Progress ── */}
        {scanning && scanProgress && (
          <div className="progress-box discover-progress-box" style={{ marginTop: 16 }}>
            <div className="discover-progress-head">
              <strong style={{ textTransform: 'capitalize' }}>
                {PHASE_LABELS[scanProgress.current_phase] || scanProgress.current_phase?.replace(/_/g, ' ')}
              </strong>
              {scanProgress.current_phase !== 'resuming' && (
                <span>{Math.round(progressPct)}%</span>
              )}
            </div>
            <div className="discover-progress-track">
              <div style={{
                width: `${progressPct}%`, background: '#174a2c', height: '100%',
                borderRadius: 4, transition: 'width 0.4s ease'
              }} />
            </div>
            <div className="discover-progress-meta">
              {/* {scanProgress.completed_items != null && scanProgress.total_items != null &&
                <span>{scanProgress.completed_items} / {scanProgress.total_items}</span>} */}
              {scanProgress.matches_found > 0 &&
                <span>{scanProgress.matches_found} phrase hits</span>}
            </div>
            {/* phase steps */}
            <div className="chips" style={{ marginTop: 10 }}>
              {PHASE_STEPS.map((ph, i) => {
                const curIdx = ALL_PHASES.indexOf(scanProgress.current_phase);
                const myIdx  = ALL_PHASES.indexOf(ph);
                const done   = myIdx < curIdx;
                const active = myIdx === curIdx;
                return (
                  <span key={ph} className="chip" style={{
                    background: done ? '#dcfce7' : active ? '#174a2c' : '#f1f5f9',
                    color:      done ? '#166534' : active ? '#fff'    : '#94a3b8',
                    fontWeight: 700,
                  }}>
                    {done ? '✓ ' : ''}{PHASE_LABEL[i]}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>
      </div>

      {/* ══ RESULTS ══════════════════════════════════════════════ */}
      {/* ══ RESULTS AND HISTORY SECTION ═════════════════════════════ */}
      <div className="card phrase-results-card">
        <div className="section-head" style={{ marginBottom: showHistory || (!results && !showHistory) ? '0' : '16px' }}>
          <div>
            <h3 className="section-title">
              {showHistory ? 'Previous Scans' : (results ? 'Search Results' : 'Ready to Search')}
            </h3>
            {!showHistory && results && (
              <p className="muted" style={{ fontSize: '0.85rem', marginTop: 4 }}>
                Target: <strong>{results.keyword_phrase}</strong>
              </p>
            )}
            {!showHistory && !results && (
              <p className="muted" style={{ fontSize: '0.85rem', marginTop: 4 }}>
                View your past search results or start a new scan.
              </p>
            )}
          </div>
          
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {(showHistory || results) && (
              <button
                className="secondary-btn"
                style={{
                  background: canDownloadCsv ? '#174a2c' : '#e2e8f0',
                  color: canDownloadCsv ? 'white' : '#64748b',
                  border: 'none',
                  textTransform: 'uppercase',
                  fontWeight: 700,
                  padding: '8px 16px',
                  borderRadius: 6,
                  cursor: canDownloadCsv ? 'pointer' : 'not-allowed'
                }}
                onClick={handleDownloadCsv}
                disabled={!canDownloadCsv}
                title={showHistory ? 'Download CSV for this history page' : 'Download CSV for current scan results'}
              >
                Download CSV
              </button>
            )}
            <button
              className="secondary-btn"
              style={{ 
                background: '#dc2626', color: 'white', border: 'none',
                textTransform: 'uppercase', fontWeight: 600, padding: '8px 16px', borderRadius: 6
              }}
              onClick={() => {
                if (!showHistory) {
                  fetchHistoricalScans(0, true);
                  setCurrentHistoryPage(0);
                }
                setShowHistory(!showHistory);
              }}
            >
              {showHistory ? 'Back' : 'History'}
            </button>
          </div>
        </div>

        {showHistory ? (
          /* ── HISTORY VIEW ── */
          <div style={{ padding: '0' }}>
            {historyLoading ? (
              <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                <p className="muted">Loading history...</p>
              </div>
            ) : !historyItems[currentHistoryPage] ? (
              <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                <p className="muted">No past scans found.</p>
              </div>
            ) : (
              (() => {
                const currentScan = historyItems[currentHistoryPage];
                if (!currentScan) return null;
                const historyHasMore = Boolean(historyHasMoreByPage[currentHistoryPage]);
                const totalScansLabel = `${currentHistoryPage + 1}${historyHasMore ? '+' : ` of ${historyItems.filter(Boolean).length}`}`;

                return (
                  <div>
                    <div style={{ padding: '20px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                      <div style={{ marginBottom: 12 }}>
                        <p style={{ margin: '0 0 8px 0', fontSize: '1.1rem', fontWeight: 600 }}>
                          <strong>Target:</strong> {currentScan.keywordQuery || (Array.isArray(currentScan.keywords) ? currentScan.keywords.join(', ') : currentScan.keywords)}
                        </p>
                        <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b' }}>
                          Scan Date: {currentScan.createdAt?.toDate ? currentScan.createdAt.toDate().toLocaleString() : (currentScan.createdAtMs ? new Date(currentScan.createdAtMs).toLocaleString() : 'Unknown')}
                        </p>
                      </div>
                      
                      {(() => {
                        const allGrps = (currentScan.groupsDetails || []).filter(isValidDiscoveryGroup);
                        const phraseCount = allGrps.filter(g => (g.phraseHits || 0) > 0).length;
                        const analysisOnly = allGrps.length - phraseCount;
                        const overallFraudCount = allGrps.filter(g => getDecisionLabels(g).overallLabel === 'fraud').length;
                        const overallUncertainCount = allGrps.filter(g => getDecisionLabels(g).overallLabel === 'uncertain').length;
                        const overallGenuineCount = allGrps.filter(g => getDecisionLabels(g).overallLabel === 'genuine').length;
                        
                        return (
                          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                            <span className="chip">Discovered: {allGrps.length}</span>
                            <span className="chip" style={{ background: '#dcfce7', color: '#166534' }}>Phrase: {phraseCount}</span>
                            <span className="chip" style={{ background: '#e0f2fe', color: '#075985' }}>Analysis: {analysisOnly}</span>
                            <span className="chip" style={{ background: '#fff7ed', color: '#c2410c' }}>Overall Fraud: {overallFraudCount}</span>
                            <span className="chip" style={{ background: '#fffbeb', color: '#b45309' }}>Overall Uncertain: {overallUncertainCount}</span>
                            <span className="chip" style={{ background: '#f0fdf4', color: '#166534' }}>Overall Genuine: {overallGenuineCount}</span>
                          </div>
                        );
                      })()}
                    </div>
                    {(() => {
                      const validGroups = historySortedGroups;
                      return (
                        <>
                          <div className="phrase-filter-row" style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '24px',
                            marginBottom: '24px',
                            alignItems: 'center',
                            padding: '12px 16px',
                            background: '#f8fafc',
                            borderRadius: '12px',
                            border: '1px solid #e2e8f0'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <span style={{
                                fontSize: '0.85rem',
                                fontWeight: 700,
                                color: locationFilter === 'all' ? '#0f172a' : '#94a3b8',
                                letterSpacing: '0.02em'
                              }}>
                                ALL INTEL
                              </span>

                              <div
                                onClick={() => setLocationFilter(locationFilter === 'all' ? 'focused' : 'all')}
                                style={{
                                  width: '48px',
                                  height: '24px',
                                  background: locationFilter === 'focused' ? '#174a2c' : '#cbd5e1',
                                  borderRadius: '24px',
                                  position: 'relative',
                                  cursor: 'pointer',
                                  transition: 'all 0.3s ease',
                                  boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.06)'
                                }}
                              >
                                <div style={{
                                  width: '18px',
                                  height: '18px',
                                  background: '#ffffff',
                                  borderRadius: '50%',
                                  position: 'absolute',
                                  top: '3px',
                                  left: locationFilter === 'focused' ? '27px' : '3px',
                                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                }} />
                              </div>

                              <span style={{
                                fontSize: '0.85rem',
                                fontWeight: 700,
                                color: locationFilter === 'focused' ? '#174a2c' : '#94a3b8',
                                letterSpacing: '0.02em',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}>
                                LOCATION FOCUS
                              </span>
                            </div>

                            <div style={{ width: '1px', height: '24px', background: '#e2e8f0' }} />

                            <div className="chips" style={{ gap: '8px' }}>
                              {['All', 'Overall-Fraud', 'Overall-Uncertain', 'Overall-Genuine'].map(f => (
                                <button
                                  key={f}
                                  className="chip"
                                  onClick={() => setOverallFilter(f)}
                                  style={{
                                    background: overallFilter === f ? '#174a2c' : '#f1f5f9',
                                    color: overallFilter === f ? '#fff' : '#475569',
                                    cursor: 'pointer',
                                    border: 'none',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    padding: '6px 12px'
                                  }}
                                >
                                  {f}
                                </button>
                              ))}
                            </div>

                            <div style={{ width: '1px', height: '24px', background: '#e2e8f0' }} />

                              <div className="chips" style={{ gap: '8px' }}>
                                {[
                                  ['recent_active', 'Recent Active'],
                                  ['member_count', 'Members High-Low'],
                                ].map(([value, label]) => (
                                <button
                                  key={value}
                                  className="chip"
                                  onClick={() => setSecondarySort(value)}
                                  style={{
                                    background: secondarySort === value ? '#0f172a' : '#f1f5f9',
                                    color: secondarySort === value ? '#fff' : '#475569',
                                    cursor: 'pointer',
                                    border: 'none',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    padding: '6px 12px'
                                  }}
                                >
                                  {label}
                                </button>
                              ))}
                            </div>

                            <div style={{ width: '1px', height: '24px', background: '#e2e8f0' }} />

                            <div className="chips" style={{ gap: '8px' }}>
                              <span className="chip" style={{ fontSize: '0.75rem', padding: '4px 10px' }}>
                                {validGroups.length} groups shown
                              </span>
                            </div>
                          </div>

                          {validGroups.length === 0 ? (
                            <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                              <p className="muted" style={{ marginBottom: 8 }}>No groups match the current history filters.</p>
                              <p className="muted" style={{ fontSize: '0.85rem' }}>
                                Try switching to <strong>All</strong> or turning off <strong>Location Focus</strong>.
                              </p>
                            </div>
                          ) : (
                          <div className="list">
                          {validGroups.map((item, idx) => {
                            const grp = item._data;
                            const rev = groupReviews[getReviewGroupKey(grp)] || getEmbeddedReviewFromGroup(grp);
                            const startsUnrelatedSection =
                              item._keywordRelatedRank < 0 &&
                              (idx === 0 || validGroups[idx - 1]?._keywordRelatedRank >= 0);
                            return (
                              <React.Fragment key={`${getReviewGroupKey(grp) || 'history'}-${idx}`}>
                                {startsUnrelatedSection && <KeywordUnrelatedDivider />}
                                <UnifiedGroupCard
                                  index={idx + 1}
                                  g={grp}
                                  review={rev}
                                  onReview={handleMarkReview}
                                  onBlockedStatusChange={handleBlockedStatusChange}
                                />
                              </React.Fragment>
                            );
                          })}
                          </div>
                          )}
                        </>
                      );
                    })()}

                    {(currentHistoryPage > 0 || historyHasMore) && (
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 14, padding: '20px', borderTop: '1px solid #e2e8f0', background: '#f8fafc' }}>
                        <button
                          className="secondary-btn"
                          onClick={() => setCurrentHistoryPage(p => Math.max(0, p - 1))}
                          disabled={currentHistoryPage === 0}
                          style={{
                            minWidth: 120,
                            height: 42,
                            padding: '0 16px',
                            borderRadius: 999,
                            border: currentHistoryPage === 0 ? '1px solid #e2e8f0' : '1px solid #cbd5e1',
                            background: currentHistoryPage === 0 ? '#f8fafc' : '#ffffff',
                            color: currentHistoryPage === 0 ? '#94a3b8' : '#0f172a',
                            fontWeight: 700,
                            fontSize: 14,
                            boxShadow: currentHistoryPage === 0 ? 'none' : '0 8px 18px rgba(15, 23, 42, 0.06)',
                          }}
                        >
                          ← Previous
                        </button>
                        <span style={{
                          minWidth: 110,
                          height: 42,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '0 16px',
                          borderRadius: 999,
                          background: '#ffffff',
                          border: '1px solid #e2e8f0',
                          fontWeight: 800,
                          fontSize: 14,
                          color: '#0f172a',
                          boxShadow: '0 8px 18px rgba(15, 23, 42, 0.05)',
                        }}>
                          Scan {totalScansLabel}
                        </span>
                        <button
                          className="secondary-btn"
                          onClick={() => fetchHistoricalScans(currentHistoryPage + 1)}
                          disabled={!historyHasMore}
                          style={{
                            minWidth: 100,
                            height: 42,
                            padding: '0 16px',
                            borderRadius: 999,
                            border: !historyHasMore ? '1px solid #e2e8f0' : '1px solid #174a2c',
                            background: !historyHasMore ? '#f8fafc' : '#174a2c',
                            color: !historyHasMore ? '#94a3b8' : '#ffffff',
                            fontWeight: 700,
                            fontSize: 14,
                            boxShadow: !historyHasMore ? 'none' : '0 10px 20px rgba(23, 74, 44, 0.18)',
                          }}
                        >
                          Next →
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()
            )}
          </div>
        ) : results ? (
          /* ── CURRENT RESULTS VIEW ── */
          <>
            <div className="chips phrase-block-small-gap" style={{ gap: '10px', flexWrap: 'wrap', marginBottom: 16 }}>
              <span className="chip">Discovered: {stats.total}</span>
              <span className="chip" style={{ background: '#dcfce7', color: '#166534' }}>Phrase: {stats.phrase}</span>
              <span className="chip" style={{ background: '#e0f2fe', color: '#075985' }}>Analysis: {stats.discovery}</span>
              <span className="chip" style={{ background: '#fff7ed', color: '#c2410c' }}>Overall Fraud: {stats.overallFraud}</span>
              <span className="chip" style={{ background: '#fffbeb', color: '#b45309' }}>Overall Uncertain: {stats.overallUncertain}</span>
              <span className="chip" style={{ background: '#f0fdf4', color: '#166534' }}>Overall Genuine: {stats.overallGenuine}</span>
            </div>

            {results.translations && Object.keys(results.translations).length > 0 && (
              <div className="phrase-translation-box" style={{ marginBottom: 20 }}>
                <div className="phrase-translation-title-row">
                  <strong>Translation mapping used</strong>
                  <span className="pill">{Object.keys(results.translations).length} phrases</span>
                </div>
                <div className="phrase-translation-list">
                  {Object.entries(results.translations).map(([orig, langs]) => (
                    <div key={orig} className="phrase-translation-item">
                      <div className="phrase-translation-key">"{orig}"</div>
                      <div className="phrase-translation-value">
                        {Object.entries(langs).map(([lang, value]) => `${lang}: ${value}`).join(' · ')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}


            <div className="phrase-filter-row telegram-filter-panel" style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: '24px', 
              marginBottom: '24px', 
              alignItems: 'center',
              padding: '12px 16px',
              background: '#f8fafc',
              borderRadius: '12px',
              border: '1px solid #e2e8f0'
            }}>
              {/* ── Slidable Goa Filter ── */}
              <div className="telegram-filter-toggle-row" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ 
                  fontSize: '0.85rem', 
                  fontWeight: 700, 
                  color: locationFilter === 'all' ? '#0f172a' : '#94a3b8',
                  letterSpacing: '0.02em'
                }}>
                  ALL INTEL
                </span>
                
                <div 
                  onClick={() => setLocationFilter(locationFilter === 'all' ? 'focused' : 'all')}
                  style={{
                    width: '48px',
                    height: '24px',
                    background: locationFilter === 'focused' ? '#174a2c' : '#cbd5e1',
                    borderRadius: '24px',
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.06)'
                  }}
                >
                  <div style={{
                    width: '18px',
                    height: '18px',
                    background: '#ffffff',
                    borderRadius: '50%',
                    position: 'absolute',
                    top: '3px',
                    left: locationFilter === 'focused' ? '27px' : '3px',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }} />
                </div>

                <span style={{ 
                  fontSize: '0.85rem', 
                  fontWeight: 700, 
                  color: locationFilter === 'focused' ? '#174a2c' : '#94a3b8',
                  letterSpacing: '0.02em',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  LOCATION FOCUS
                </span>
              </div>

              <div className="telegram-filter-divider" style={{ width: '1px', height: '24px', background: '#e2e8f0' }} />

              {/* ── AI Status Filter ── */}
              <div className="chips telegram-filter-chip-row" style={{ gap: '8px' }}>
                {['All', 'Overall-Fraud', 'Overall-Uncertain', 'Overall-Genuine'].map(f => (
                  <button
                    key={f}
                    className="chip"
                    onClick={() => setOverallFilter(f)}
                    style={{
                      background: overallFilter === f ? '#174a2c' : '#f1f5f9',
                      color: overallFilter === f ? '#fff' : '#475569',
                      cursor: 'pointer',
                      border: 'none',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      padding: '6px 12px'
                    }}
                  >
                    {f}
                  </button>
                ))}
              </div>

              <div className="telegram-filter-divider" style={{ width: '1px', height: '24px', background: '#e2e8f0' }} />

              <div className="chips telegram-filter-chip-row" style={{ gap: '8px' }}>
                {[
                  ['recent_active', 'Recent Active'],
                  ['member_count', 'Members High-Low'],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    className="chip"
                    onClick={() => setSecondarySort(value)}
                    style={{
                      background: secondarySort === value ? '#0f172a' : '#f1f5f9',
                      color: secondarySort === value ? '#fff' : '#475569',
                      cursor: 'pointer',
                      border: 'none',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      padding: '6px 12px'
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="telegram-filter-divider" style={{ width: '1px', height: '24px', background: '#e2e8f0' }} />

              <div className="chips telegram-filter-chip-row telegram-filter-summary-row" style={{ gap: '8px' }}>
                <span className="chip" style={{ fontSize: '0.75rem', padding: '4px 10px' }}>
                  {filteredItems.length} groups shown
                </span>
                {locationFilter === 'all' && (
                  <span className="chip" style={{ background: '#fef3c7', color: '#92400e', fontSize: '0.75rem', padding: '4px 10px' }}>
                    Showing all locations
                  </span>
                )}
              </div>

            </div>

            {filteredItems.length === 0 ? (
              <div className="card" style={{ textAlign: 'center', padding: '40px 20px', background: 'transparent', border: '1px dashed #cbd5e1' }}>
                <p className="muted">No results match the selected filters.</p>
              </div>
            ) : (
              <div className="list">
                {filteredItems.map((item, idx) => {
                  const rev = groupReviews[getReviewGroupKey(item._data)];
                  const startsUnrelatedSection =
                    item._keywordRelatedRank < 0 &&
                    (idx === 0 || filteredItems[idx - 1]?._keywordRelatedRank >= 0);
                  return (
                    <React.Fragment key={idx}>
                      {startsUnrelatedSection && <KeywordUnrelatedDivider />}
                      <UnifiedGroupCard
                        index={idx + 1}
                        g={item._data}
                        review={rev}
                        onReview={handleMarkReview}
                        onBlockedStatusChange={handleBlockedStatusChange}
                      />
                    </React.Fragment>
                  );
                })}
              </div>
            )}

            <p className="muted" style={{ textAlign: 'center', marginTop: 24, fontSize: 12 }}>
              {results.scan_timestamp && `Scan completed: ${new Date(results.scan_timestamp).toLocaleString()}`}
              {savedReportId && ` · DB Reference: ${savedReportId}`}
            </p>
          </>
        ) : (
          /* ── EMPTY STATE (No History, No Results) ── */
          <div style={{ padding: '32px', textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🔭</div>
            <h3 style={{ margin: '0 0 8px', color: '#0f172a' }}>Ready to Search</h3>
            <p className="muted" style={{ maxWidth: 420, margin: '0 auto', lineHeight: 1.6 }}>
              Enter a keyword above. Keywords are translated, groups are discovered across search engines, then phrase-matched or fully analysed. Expired groups are hidden automatically.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
